// Media/storage engine extracted from MineRadio; the UI is mounted only by Vue.
import Vue from 'vue';
import '@applemusic-like-lyrics/core/style.css';
import { parseBlob, parseBuffer, selectCover } from 'music-metadata';
import { DomLyricPlayer, type LyricLine as AmllLyricLine } from '@applemusic-like-lyrics/core';
import { parseAmllLyrics, toAmllLines } from '../amll-lyrics';
import { applyGeneratedTranslation, cleanAuxiliaryLyric, lyricOriginalSignature, mergeLyricLayers } from './lyric-layers';
import type { OnlineLyricBundle } from './online-lyrics';
import { activeLyricIndex, applyLyricOffset, parseTimedLyrics, type LyricLine } from '../lyrics';
import { estimatePersistentImport, importFingerprint, importRelativePath, isAudioImport, isLyricImport, isQuotaExceededError } from '../import-utils';
import { BlobUrlRegistry, PlayerController, clampSeekTime, type ResolvePurpose } from '../player-controller';
import { parseBackup, serializeBackup } from '../metadata-backup';
import { apiRequestTarget, apiResourceTarget, normalizeApiBaseUrl } from './api-transport';
import { browserDav, mergeBrowserDavScan } from './pages-webdav';
import { resolveBrowserPlayback, resolveBrowserDownload, approvedDirectMediaUrl } from './browser-online-sources';
import { resolveOnlineWithFallback, shouldRelayNativePlayback } from './online-transport';
import { probePlaybackMetadata } from './playback-metadata-probe';
import { isCloudflareRuntimeHost } from './deployment-runtime';
import { trackListening } from './listening';
import { normalizeHistoryPlayedAt, recordHistoryPlayback, type HistoryPlayedAt } from './play-history';
import { isLibraryTrack, migrateLibraryMembership } from './library-membership';
import { libraryCloudInput, mergeLibraryCloudIndex } from './library-cloud-merge';
import { serializeLibraryCloudSnapshot } from '../library-cloud-snapshot';
import type { MetadataBackup } from '../metadata-backup';
import { sourceErrorText } from './source-error';
import { migratePlatformDefaultQualities, platformQualityLabel, platformQualityOptions, qualityPlatform } from './platform-quality';
import { LOCAL_PRESENTATION_VERSION, hasMisreadLyricist, localPresentationMetadata, readEmbeddedLyrics, type EmbeddedLyrics } from '../metadata';
import { readLocalPresentations, storeLocalPresentation, thumbnailArtwork, type LocalPresentation } from './local-presentation-cache';
import { compactLocalPresentationSnapshot } from './local-presentation-snapshot';
import { trackMetadataEditCapability } from './track-metadata-edit';
import { localLyricAttemptKey, localLyricLookupQuery, releaseLocalLyricAttempt, reserveLocalLyricAttempt, selectLocalLyricCandidate } from './local-lyrics';
import { localFileRecoveryError, localFileSampleSignature } from '../local-file-recovery';
import { readLyricText } from '../lyric-text';
import { assertPlaylistCoverBudget, readPlaylistCoverRecords, verifyPlaylistCover, type PlaylistCoverFields } from '../playlist-cover';
import { trackArtworkCandidate } from './playlist-artwork';
import { awaitMediaOperation, waitForMediaPosition } from './quality-preparation';
import { NativeAudioOutput, releaseNativeMedia, type NativeOutputIntent } from './native-audio-output';
import { equalizer, attachLocalEqualizer, detachEqualizer, isEqualizerBound, setEqualizerOwner, markEqualizerBypass } from './equalizer';
import { audioCacheErrorText } from './audio-cache-error';

type View = 'home' | 'library' | 'playlists' | 'sources' | 'settings' | 'now-playing';
export type Track = {
  albumId?: string; artistIds?: string[]; metadata?: Record<string, any>;
  id: string; sourceType: 'local' | 'webdav' | 'online'; sourceId: string; externalId: string;
  title: string; artists: string[]; album: string; durationMs?: number; availability: 'session' | 'persistent' | 'available' | 'remote' | 'unavailable';
  fileName?: string; fingerprint?: string; size?: number; artworkRef?: string; customCover?: string; lyrics?: LyricLine[]; amllLyrics?: AmllLyricLine[]; createdAt: string; updatedAt: string;
};
type Playlist = { id: string; name: string; description?: string; trackIds: string[]; createdAt: string; updatedAt?: string } & PlaylistCoverFields;
type Source = { id: string; name: string; kind: string; status: string; capabilities: string[]; platforms?: string[]; qualitiesByPlatform?: Record<string, string[]>; reason?: string; enabled?: boolean; lastCheckedAt?: string };
type OnlineSelection = { sourceId: string; quality: string };
type OnlineTransport = 'browser' | 'relay';
type OnlineRequest = OnlineSelection & { transport: OnlineTransport };
type OnlineResolution = OnlineSelection & { trackId: string; requestedQuality: string; effectiveQuality: string; fallback: boolean; attemptedQualities: string[]; format: string; qualityVerified: boolean; transport: OnlineTransport; expiresAt?: string; nativeCandidate?: boolean; byteVerified?: boolean; playbackVerified?: boolean; codec?: string; bitrate?: number | null; sampleRate?: number | null; bitsPerSample?: number | null; channels?: number | null; encodedDuration?: number | null; bitrateBasis?: 'mpeg-frame' | null };
type PreparedOnlinePlayback = { trackId: string; exactTrackId: string; sourceId: string; requestedQuality: string; url: string; resolution: OnlineResolution; expiresAt: number };
type PreparedQualityPlayback = PreparedOnlinePlayback & { media: HTMLAudioElement };
type PlaybackIntent = { id: string; autoplay: boolean; startPosition: number; playbackRate?: number; preserveCurrentState?: boolean; preparedPlayback?: PreparedOnlinePlayback; onlineRequest?: OnlineRequest };
type QualitySwitchTask = { trackId: string; controller: AbortController; candidate: HTMLAudioElement | null; phase: 'prepare' | 'handoff'; intent: PlaybackIntent | null; nativePromotion?: boolean; baseline: { resolution: OnlineResolution | null; url: string | null; position: number; autoplay: boolean; playbackRate: number } };
type PlaybackMode = 'sequence' | 'repeat-all' | 'repeat-one' | 'shuffle';
type MotionLevel = 'high' | 'medium' | 'low' | 'off';
type PlaybackFailureStage = 'resolve' | 'play';
export type PlaybackFailure = { trackId: string; title: string; artists: string[]; source: string; stage: PlaybackFailureStage; message: string };
type SavedState = { tracks: Track[]; queue: string[]; history: string[]; historyPlayedAt: HistoryPlayedAt; currentId: string | null; favorites: string[]; playlists: Playlist[]; theme: 'light' | 'dark' | 'system'; playMode: PlaybackMode; positions: Record<string, number>; lyrics: Record<string, LyricLine[]>; lyricOffsetMs: number; motionLevel: MotionLevel; volume: number };
type ImportFailure = { fileName: string; reason: string };
type ImportTask = { status: 'running' | 'completed' | 'cancelled'; total: number; processed: number; audioTotal: number; lyricTotal: number; imported: number; failed: ImportFailure[]; persistent: boolean; startedAt: string; finishedAt?: string; playlistId?: string; playlistAdded?: number };
type AppState = SavedState & { view: View; searchQuery: string; remoteSearchResults: Track[]; selectedPlaylistId: string | null; notice: { text: string; type: 'info' | 'success' | 'error' } | null; playbackFailure: PlaybackFailure | null; currentTime: number; duration: number; isPlaying: boolean; sources: Source[]; davConnections: DavConnection[]; importTask: ImportTask | null };
export type DavConnection = { id: string; name: string; url: string; rootPath?: string; credentialConfigured?: boolean; createdAt?: string; updatedAt?: string };
export type DavConnectionInput = { name: string; url: string; rootPath?: string; username?: string; password?: string; token?: string };

const STORAGE_KEY = 'mineradio.state.v1';
const AUDIO_DB = 'mineradio.audio.v1';
const AUDIO_STORE = 'blobs';
let audio = new Audio();
let nativeOutput: NativeAudioOutput;
const objectUrls = new BlobUrlRegistry(32);
const sessionFiles = new Map<string, Blob>();
let protectedAudioId: string | null = null;
// A new playback request invalidates the previous media element state before
// its resolver returns. This matters when the same track is selected again:
// an old timeupdate/pause/metadata event can otherwise pass the ID check while
// the new request is still resolving and briefly restore the old position.
const invalidateProtectedAudio = () => {
  if (protectedAudioId) objectUrls.unprotect(protectedAudioId);
  protectedAudioId = null;
};
let removeResumeSeek: (() => void) | null = null;
let activeMediaSignal: AbortSignal | null = null;
let pendingPlaybackMetadataProbe: { resolution: OnlineResolution; trackId: string; exactTrackId: string; mediaUrl: string; signal: AbortSignal } | null = null;
let prefetchedPlayback: { trackId: string; url: string; resolution: OnlineResolution | null; exactTrackId?: string; expiresAt: number } | null = null;
let qualitySwitchTask: QualitySwitchTask | null = null;
const artworkUrls = new Map<string, string>();
let mediaSessionBound = false;
let mediaMetadataKey = '';
let renderedLyricIndex = -2;
let amllLyricPlayer: DomLyricPlayer | null = null;
let amllLyricFrame = 0;
const VIRTUAL_TRACK_THRESHOLD = 120;
const VIRTUAL_TRACK_ROW_HEIGHT = 66;
const VIRTUAL_TRACK_OVERSCAN = 8;
let disposeVirtualTrackList: (() => void) | null = null;
const $ = <T extends Element>(selector: string) => document.querySelector<T>(selector);
const esc = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));
const uid = () => crypto.randomUUID();
const formatTime = (ms: number | undefined) => {
  const seconds = Math.max(0, Math.round((ms || 0) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};
const readSaved = (): SavedState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (parsed) return { tracks: (parsed.tracks || []).map((track: Track) => ({ ...track, customCover: track.sourceType === 'local' ? readPlaylistCoverRecords([track])[0].customCover : undefined, artworkRef: track.artworkRef?.startsWith('https://') ? track.artworkRef : undefined, availability: track.sourceType === 'local' && track.availability !== 'persistent' ? 'unavailable' : track.availability })), queue: parsed.queue || [], history: parsed.history || [], historyPlayedAt: normalizeHistoryPlayedAt(parsed.historyPlayedAt, parsed.history || []), currentId: parsed.currentId || null, favorites: parsed.favorites || [], playlists: readPlaylistCoverRecords<Playlist>(parsed.playlists || []), theme: parsed.theme || 'system', playMode: parsed.playMode || 'sequence', positions: Object.fromEntries(Object.entries(parsed.positions || {}).filter(([, value]) => Number.isFinite(value) && Number(value) >= 0).map(([id, value]) => [id, Number(value)])), lyrics: parsed.lyrics || {}, lyricOffsetMs: Number.isFinite(parsed.lyricOffsetMs) ? Math.max(-5000, Math.min(5000, parsed.lyricOffsetMs)) : 0, volume: Number.isFinite(parsed.volume) ? Math.max(0, Math.min(1, parsed.volume)) : 0.8, motionLevel: ['high', 'medium', 'low', 'off'].includes(parsed.motionLevel) ? parsed.motionLevel : 'medium' };
  } catch { /* corrupted local state is recoverable */ }
  return { tracks: [], queue: [], history: [], historyPlayedAt: {}, currentId: null, favorites: [], playlists: [], theme: 'system', playMode: 'sequence', positions: {}, lyrics: {}, lyricOffsetMs: 0, motionLevel: 'medium', volume: 0.8 };
};
const saved = readSaved();
saved.tracks = migrateLibraryMembership(saved.tracks, saved.favorites, saved.playlists);
const state: AppState = Vue.observable({ ...saved, view: 'home', searchQuery: '', remoteSearchResults: [], selectedPlaylistId: null, notice: null, playbackFailure: null, currentTime: 0, duration: 0, isPlaying: false, sources: [], davConnections: [], importTask: null });
state.volume = Number.isFinite(saved.volume) ? Math.max(0, Math.min(1, saved.volume)) : 0.8;
audio.volume = state.volume;
const apiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
const cloudflareHost = isCloudflareRuntimeHost(window.location.hostname);
export const cloudflareRuntime = cloudflareHost;
export const capabilities = Object.freeze({ webdav: true });
let sessionRefresh: Promise<void> | null = null;
async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await window.fetch(apiRequestUrl(input), init);
  if (response.status === 401) {
    const payload = await response.clone().json().catch(() => null);
    if (payload?.error?.code === 'AUTH_REQUIRED' && !cloudflareRuntime && !sessionRefresh) {
      sessionRefresh = refreshSessionAccess().finally(() => { sessionRefresh = null; });
    }
  }
  return response;
}
function apiRequestUrl(input: RequestInfo | URL): RequestInfo | URL {
  return apiRequestTarget(input, apiBaseUrl);
}
export function apiResourceUrl(value: unknown): string | null {
  return apiResourceTarget(value, apiBaseUrl, window.location.origin);
}
// Downloads own their requests and never borrow/revoke the playback URL.
export const downloadFetch = (input: RequestInfo | URL, init?: RequestInit) => apiFetch(input, init);
export async function downloadAudioBlob(track: Track): Promise<Blob | undefined> {
  if (track.sourceType !== 'local') return undefined;
  return sessionFiles.get(track.id) || (track.availability === 'persistent' ? await loadAudioBlob(track.id) : undefined);
}
let searchRequestId = 0;
let searchAbort: AbortController | null = null;
let searchTimer = 0;
let searchSource = 'all';
let searchKind: 'track' | 'album' | 'artist' | 'playlist' = 'track';
let searchComposing = false;
let searchLoading = false;
let searchCursor: string | null = null;
let searchDiagnostics: { id: string; status?: string; error?: string; resultCount?: number }[] = [];
const lyricRequests = new Map<string, { controller: AbortController; promise: Promise<void> }>();
const coverPreloads = new Map<string, HTMLImageElement>();
let activeImportAbort: AbortController | null = null;
let lastProgressPersistAt = 0;
let playbackIntent: PlaybackIntent | null = null;
type LibraryKind = 'tracks' | 'albums' | 'artists' | 'folders' | 'recent';
let libraryKind: LibraryKind = 'tracks';
let librarySource = 'all';
let librarySort = 'added';
let libraryGroup: string | null = null;
let selectedTrackDetails: string | null = null;
let editingDavId: string | null = null;
let preferredListForPlay: string[] | null = null;
const folderName = (track: Track) => {
  const file = (track.fileName || track.externalId).replaceAll('\\', '/');
  const separator = file.lastIndexOf('/');
  const parent = (separator >= 0 ? file.slice(0, separator) : '') || (track.sourceType === 'local' ? '浏览器文件' : '/');
  try { return decodeURIComponent(parent); } catch { return parent; }
};
const groupNames = (track: Track) => (libraryKind as LibraryKind) === 'albums' ? [track.album || '未知专辑'] : (libraryKind as LibraryKind) === 'artists' ? track.artists : [folderName(track)];
const libraryTracks = () => state.tracks.filter((track) => (librarySource === 'all' || track.sourceType === librarySource) && (!libraryGroup || groupNames(track).includes(libraryGroup)))
  .sort((left, right) => librarySort === 'title' ? left.title.localeCompare(right.title, 'zh-CN') : librarySort === 'artist' ? left.artists.join().localeCompare(right.artists.join(), 'zh-CN') : right.createdAt.localeCompare(left.createdAt));
let playbackError = "";
const playbackSourceLabel = (track: Track) => track.sourceType === 'online' ? `在线音源 · ${track.sourceId}` : track.sourceType === 'webdav' ? `WebDAV · ${track.sourceId}` : '本地音乐';
const rememberPlaybackFailure = (id: string, stage: PlaybackFailureStage, message: string) => {
  const track = trackById(id);
  if (!track) return;
  Vue.set(state, 'playbackFailure', { trackId: id, title: track.title, artists: [...track.artists], source: playbackSourceLabel(track), stage, message: message || '音频请求失败，请重试。' });
  state.isPlaying = false;
  persist();
  render();
};
const clearPlaybackFailure = () => {
  if (!state.playbackFailure) return;
  Vue.set(state, 'playbackFailure', null);
  render();
};
/**
 * A browser direct-media URL can pass the bounded resolver probe and still
 * fail when HTMLMediaElement starts decoding (CORS policy, CDN range handling
 * or a late network rejection).  An explicit retry of that same failed play
 * may use the server relay once.  Keep the recording/source/quality identity
 * pinned and leave the user's browser/relay preference unchanged; the relay
 * applies the same URL and media allowlist on the server side.
 */
const retryRelayRequest = (trackId: string): OnlineRequest | undefined => {
  const failure = state.playbackFailure;
  const resolved = onlinePlayback.resolved;
  if (failure?.trackId !== trackId || failure.stage !== 'play' || !resolved || resolved.trackId !== trackId || resolved.transport !== 'browser') return undefined;
  return { sourceId: resolved.sourceId, quality: resolved.requestedQuality || resolved.quality, transport: 'relay' };
};
const persist = () => {
  const { view: _view, searchQuery: _query, remoteSearchResults: _remoteSearch, notice: _notice, playbackFailure: _failure, currentTime: _time, duration: _duration, isPlaying: _playing, sources: _sources, davConnections: _dav, ...toSave } = state;
  let stage = '压缩本地展示数据'; let snapshot = '';
  try {
    stage = '序列化音乐库索引';
    snapshot = JSON.stringify(compactLocalPresentationSnapshot(toSave, persistedLocalPresentations));
    stage = '写入浏览器本地存储';
    localStorage.setItem(STORAGE_KEY, snapshot); lastFullStateSnapshot = snapshot;
    if (typeof persistencePending !== 'undefined') persistencePending = false;
    if (typeof persistenceRetryAttempts !== 'undefined') persistenceRetryAttempts = 0;
    if (typeof persistenceRetryTimer !== 'undefined' && persistenceRetryTimer !== null && typeof window !== 'undefined') window.clearTimeout(persistenceRetryTimer);
    if (typeof persistenceRetryTimer !== 'undefined') persistenceRetryTimer = null;
    if (state.notice?.text?.startsWith('音乐库索引未保存')) state.notice = null;
    if (state.importTask?.failed) state.importTask.failed = state.importTask.failed.filter(item => item.fileName !== '音乐库索引');
    return true;
  } catch (error) {
    if (typeof persistencePending !== 'undefined') persistencePending = true;
    const name = error && typeof error === 'object' && 'name' in error ? String((error as { name?: unknown }).name || 'Error') : 'Error';
    const size = snapshot ? `，快照约 ${Math.max(1, Math.round(snapshot.length / 1024))} KiB` : '';
    const reason = `音乐库索引未保存（${stage}，${name}${size}），已保留当前会话；请重试或导出备份。`;
    const task = state.importTask;
    if (task && !task.failed.some(item => item.fileName === '音乐库索引')) task.failed.push({ fileName: '音乐库索引', reason });
    state.notice = { text: reason, type: 'error' };
    if (typeof schedulePersistRetry === 'function') schedulePersistRetry();
    return false;
  }
};
let lastFullStateSnapshot: string | undefined;
let persistencePending = false;
let persistenceRetryTimer: number | null = null;
let persistenceRetryAttempts = 0;
const schedulePersistRetry = () => {
  if (!persistencePending || persistenceRetryTimer !== null || typeof window === 'undefined' || typeof window.setTimeout !== 'function' || persistenceRetryAttempts >= 2) return;
  const delay = persistenceRetryAttempts === 0 ? 500 : 2000; persistenceRetryAttempts += 1;
  persistenceRetryTimer = window.setTimeout(() => { persistenceRetryTimer = null; if (persistencePending) persist(); }, delay);
};
const retryPersist = () => persist();
const persistIfStorageUnchanged = () => {
  try { if (lastFullStateSnapshot !== undefined && localStorage.getItem(STORAGE_KEY) === lastFullStateSnapshot) persist(); }
  catch { /* Background restoration must not overwrite a newer shared snapshot. */ }
};
function clearPlaybackHistory() {
  let snapshot: string | null, next: string;
  try {
    snapshot = localStorage.getItem(STORAGE_KEY);
    const latest = JSON.parse(snapshot || 'null');
    if (!Array.isArray(latest?.tracks) || !Array.isArray(latest?.history)) throw new Error('播放记录尚未保存，请先重试保存音乐库');
    next = JSON.stringify({ ...latest, history: [], historyPlayedAt: {} });
    if (localStorage.getItem(STORAGE_KEY) !== snapshot) throw new Error('播放记录已在其他页面更新，请重试');
    localStorage.setItem(STORAGE_KEY, next);
  } catch (error) { throw new Error((error as Error).message?.startsWith('播放记录') ? (error as Error).message : '无法保存清空操作，播放记录已保留'); }
  state.history = []; state.historyPlayedAt = {};
  // Only advance the baseline when this tab owned the snapshot. A newer tab's
  // catalog must remain protected from a later passive presentation save.
  if (lastFullStateSnapshot === snapshot) lastFullStateSnapshot = next;
  notify('播放记录已清空', 'success');
}
const persistPlaybackHistory = (id: string, playedAt: number) => {
  try {
    const latest = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!Array.isArray(latest?.tracks) || !latest.tracks.some((track: Track) => track?.id === id)) return;
    const history = recordHistoryPlayback(Array.isArray(latest.history) ? latest.history : [], latest.historyPlayedAt, id, playedAt);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...latest, ...history }));
  } catch { /* Keep actual playback usable when shared storage is unavailable. */ }
};
const persistPlaybackPosition = () => {
  const id = state.currentId;
  const position = audio.currentTime;
  if (!id || protectedAudioId !== id || audio.readyState < audio.HAVE_METADATA || audio.ended || !Number.isFinite(position) || position < 0) return;
  state.positions[id] = position;
  try {
    // Passive audio/unload events must not overwrite a newer library saved by
    // another tab. Only merge this loaded audio's last position if it still exists.
    const latest = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!Array.isArray(latest?.tracks) || !latest.tracks.some((track: Track) => track?.id === id)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...latest, positions: { ...latest.positions, [id]: position } }));
  } catch { /* Leave an unreadable or unavailable shared store unchanged. */ }
};
const notify = (text: string, type: 'info' | 'success' | 'error' = 'info') => { state.notice = { text, type }; render(); window.setTimeout(() => { if (state.notice?.text === text) { state.notice = null; render(); } }, 4200); };
const trackById = (id: string | null) => state.tracks.find((track) => track.id === id);
const retainSearchTrack = (id: string | null) => {
  const stored = trackById(id); if (stored) return stored;
  const result = state.remoteSearchResults.find((track) => track.id === id);
  if (result) { state.tracks.push(result); persist(); }
  return result;
};
const currentTrack = () => trackById(state.currentId);
const pruneTrackReferences = () => {
  const ids = new Set(state.tracks.map((track) => track.id));
  state.queue = state.queue.filter((id) => ids.has(id)); state.history = state.history.filter((id) => ids.has(id)); state.favorites = state.favorites.filter((id) => ids.has(id));
  state.historyPlayedAt = normalizeHistoryPlayedAt(state.historyPlayedAt, state.history);
  state.playlists.forEach((playlist) => { playlist.trackIds = playlist.trackIds.filter((id) => ids.has(id)); });
  for (const id of Object.keys(state.positions)) if (!ids.has(id)) delete state.positions[id];
  for (const id of Object.keys(state.lyrics)) if (!ids.has(id)) delete state.lyrics[id];
  if (state.currentId && !ids.has(state.currentId)) {
    cancelPlaybackRequest();
    state.currentId = null; state.isPlaying = false; state.currentTime = 0; state.duration = 0;
    audio.pause(); audio.removeAttribute('src'); audio.load();
  }
};
function currentPlaybackTime() {
  const value = state.currentId && protectedAudioId === state.currentId && Number.isFinite(audio.currentTime) ? audio.currentTime : state.currentTime;
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}
const seekAudio = (target: number) => {
  const switching = qualitySwitchTask;
  if (switching?.trackId === state.currentId && switching.intent && switching.phase !== 'prepare') {
    const position = clampSeekTime(target, state.duration, []);
    if (position === null) return false;
    switching.intent.startPosition = position;
    state.currentTime = position; state.positions[switching.trackId] = position; ui.positionRevision += 1;
    try { audio.currentTime = position; } catch { /* The preparation wait applies the latest intent once metadata arrives. */ }
    persist(); return true;
  }
  if (!state.currentId || protectedAudioId !== state.currentId || audio.readyState < audio.HAVE_METADATA) return false;
  const ranges: [number, number][] = [];
  for (let index = 0; index < audio.seekable.length; index += 1) ranges.push([audio.seekable.start(index), audio.seekable.end(index)]);
  const position = clampSeekTime(target, audio.duration, ranges);
  if (position === null) return false;
  try {
    audio.currentTime = position;
    state.currentTime = position;
    ui.positionRevision += 1;
    if (state.currentId) state.positions[state.currentId] = position;
    persist();
    return true;
  } catch { return false; }
};
const sameIds = (left: readonly string[], right: readonly string[]) => left.length === right.length && left.every((id, index) => id === right[index]);
function waitForAudioMetadata(signal: AbortSignal | null): Promise<void> {
  const media = audio;
  const mediaError = () => new Error(`媒体错误 ${media.error?.code || 0}: ${media.error?.message || '音频流无法载入或解码'}`);
  if (signal?.aborted) return Promise.reject(new DOMException('播放请求已取消', 'AbortError'));
  if (media.error) return Promise.reject(mediaError());
  if (media.readyState >= media.HAVE_METADATA) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const finish = (error?: Error) => {
      clearTimeout(timer);
      media.removeEventListener('loadedmetadata', ready);
      media.removeEventListener('error', failed);
      signal?.removeEventListener('abort', aborted);
      if (error) reject(error); else resolve();
    };
    const ready = () => finish();
    const failed = () => finish(mediaError());
    const aborted = () => finish(new DOMException('播放请求已取消', 'AbortError'));
    const timer = window.setTimeout(() => finish(new Error('音频元数据载入超时')), 15000);
    media.addEventListener('loadedmetadata', ready, { once: true });
    media.addEventListener('error', failed, { once: true });
    signal?.addEventListener('abort', aborted, { once: true });
  });
}
/**
 * The persistent queue is an explicit playback context only while the
 * current track belongs to it. Starting a song from the library/search keeps
 * the active list in that view, so Next cannot unexpectedly jump into an old
 * queue that happens to be stored in localStorage.
 */
const activePlaybackList = () => {
  const allIds = state.tracks.map((track) => track.id);
  if (preferredListForPlay) return preferredListForPlay.filter((id) => allIds.includes(id));
  if (state.queue.length && (!state.currentId || state.queue.includes(state.currentId))) {
    return state.queue.filter((id) => allIds.includes(id));
  }
  return allIds;
};
function onlineResolutionFromData(trackId: string, selection: OnlineSelection, exactTrackId: string, data: Record<string, any>) {
  const effectiveQuality = String(data.effectiveQuality || data.quality || selection.quality);
  if (effectiveQuality !== selection.quality && (data.fallback !== true || data.requestedQuality !== selection.quality)) throw new Error('音源返回了不同档位，但没有提供可验证的降档结果，请重新尝试。');
  const url = data.transport === 'browser' ? approvedDirectMediaUrl(data.streamUrl) : apiResourceUrl(data.streamUrl);
  if (!url) throw new Error('接口未返回有效音频地址');
  const resolution: OnlineResolution = {
    ...selection, quality: effectiveQuality, requestedQuality: selection.quality, effectiveQuality,
    fallback: data.fallback === true, attemptedQualities: Array.isArray(data.attemptedQualities) ? data.attemptedQualities : [effectiveQuality],
    trackId, qualityVerified: data.qualityVerified === true, transport: data.transport,
    // Browser adapters estimate a local TTL; only relay tokens expose an
    // enforced expiry that can safely trigger renewal before resuming.
    expiresAt: data.transport === 'relay' && typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
    format: String(data.format || data.mimeType || '').replace(/^audio\//, '').toUpperCase(),
    nativeCandidate: data.nativeCandidate === true, byteVerified: data.byteVerified === true, playbackVerified: false,
    codec: data.codec, bitrate: data.bitrate, sampleRate: data.sampleRate, bitsPerSample: data.bitsPerSample, channels: data.channels, encodedDuration: data.encodedDuration, bitrateBasis: data.bitrateBasis,
  };
  return { exactTrackId, url, resolution };
}
async function resolvePrivateCloudPlayback(track: Track, quality: string, signal: AbortSignal) {
  const { resolveCloudPlayback } = await import('./accounts');
  const data = await resolveCloudPlayback(track, quality, signal);
  return { ...data, transport: 'relay' as const };
}
const playbackController = new PlayerController({
  resolveSource: async (id, signal, purpose: ResolvePurpose = 'play') => {
    const prefetch = purpose === 'prefetch';
    if (!prefetch) {
      activeMediaSignal = signal;
      pendingPlaybackMetadataProbe = null;
    }
    const track = trackById(id);
    if (!track) return null;
    if (!prefetch) playbackError = '';
    const localUrl = objectUrls.get(id);
    if (localUrl) return localUrl;
    if (track.sourceType === 'local') {
      const file = sessionFiles.get(id) || (track.availability === 'persistent' ? await loadAudioBlob(id) : undefined);
      if (signal.aborted) return null;
      if (file) return objectUrls.create(file, file.type, id);
      if (!prefetch) track.availability = 'unavailable';
      return null;
    }
    if (track.sourceType !== 'webdav' && track.sourceType !== 'online') return null;
    const pinnedRequest = !prefetch && playbackIntent?.id === id ? playbackIntent.onlineRequest : undefined;
    const selection = pinnedRequest || onlineSelection(track);
    const prepared = !prefetch && playbackIntent?.id === id ? playbackIntent.preparedPlayback : undefined;
    if (track.sourceType === 'online' && prepared) {
      const exactTrackId = selection.sourceId === track.sourceId ? track.id : `online:${selection.sourceId}:${onlinePlatform(track)}:${track.externalId}`;
      if (prepared.trackId !== id || prepared.exactTrackId !== exactTrackId || prepared.sourceId !== selection.sourceId
        || prepared.resolution.trackId !== id || prepared.resolution.sourceId !== selection.sourceId
        || prepared.requestedQuality !== selection.quality || prepared.resolution.requestedQuality !== selection.quality
        || prepared.expiresAt <= Date.now() || signal.aborted) throw new Error('已准备的音质地址已失效，请重新切换');
      onlinePlayback.resolved = prepared.resolution;
      pendingPlaybackMetadataProbe = { resolution: prepared.resolution, trackId: id, exactTrackId: prepared.exactTrackId, mediaUrl: prepared.url, signal };
      return prepared.url;
    }
    if (track.sourceType === 'online' && track.sourceId === 'netease-cloud' && track.metadata?.neteaseCloud?.accountOnly === true) {
      try {
        const data = await resolvePrivateCloudPlayback(track, selection.quality, signal);
        if (signal.aborted) return null;
        const { url, resolution } = onlineResolutionFromData(id, { sourceId: 'netease-cloud', quality: selection.quality }, id, data);
        if (prefetch) prefetchedPlayback = { trackId: id, url, resolution, expiresAt: Math.min(Date.now() + 30_000, Date.parse(resolution.expiresAt || '') - 5000 || Infinity) };
        else onlinePlayback.resolved = resolution;
        return url;
      } catch (error) { if (!prefetch && !signal.aborted) playbackError = (error as Error).message; throw error; }
    }
    if (track.sourceType === 'online') {
      try {
        const exactId = selection.sourceId === track.sourceId ? track.id : `online:${selection.sourceId}:${onlinePlatform(track)}:${track.externalId}`;
        const data = await resolveOnlineRequest(exactId, selection.quality, signal, pinnedRequest?.transport);
        if (signal.aborted) return null;
        const { url, resolution } = onlineResolutionFromData(id, selection, exactId, data);
        if (prefetch) prefetchedPlayback = { trackId: id, url, resolution, exactTrackId: exactId, expiresAt: Math.min(Date.now() + 30_000, Date.parse(resolution.expiresAt || '') - 5000 || Infinity) };
        else {
          onlinePlayback.resolved = resolution;
          pendingPlaybackMetadataProbe = { resolution, trackId: id, exactTrackId: exactId, mediaUrl: url, signal };
        }
        return url;
      } catch (error) {
        if (!prefetch && !signal.aborted) playbackError = `在线源当前无法播放该歌曲：${sourceErrorText(error)}`;
        return null;
      }
    }
    const response = cloudflareRuntime
      ? await apiFetch('/api/webdav/stream-token', browserDav.request(track.sourceId, { href: track.externalId, indexToken: track.metadata?.davIndexToken }, signal))
      : await apiFetch(`/api/webdav/connections/${encodeURIComponent(track.sourceId)}/stream-token`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ href: track.externalId }), signal,
    });
    const payload = await response.json().catch(() => null);
    if (signal.aborted) return null;
    if (!response.ok) {
      const code = payload?.error?.code || payload?.code;
      const message = typeof payload?.error === 'string' ? payload.error : payload?.error?.message;
      if (!prefetch) playbackError = message || code || `请求失败 (${response.status})`;
      return null;
    }
    const data = payload?.data;
    const url = apiResourceUrl(data?.streamUrl);
    return url;
  },
  prefetchKey: (id) => {
    const track = trackById(id);
    if (!track || track.sourceType !== 'online') return `${id}:${track?.updatedAt || ''}`;
    const pinned = playbackIntent?.id === id ? playbackIntent.onlineRequest : undefined;
    const selection = pinned || onlineSelection(track);
    return `${id}:${selection.sourceId}:${selection.quality}:${pinned?.transport || onlinePlayback.transport}`;
  },
  onPrefetchUsed: (id, url, signal) => {
    const track = trackById(id);
    if (!track) return false;
    const prepared = prefetchedPlayback;
    if (track.sourceType === 'online' && (!prepared || prepared.trackId !== id || prepared.url !== url || prepared.expiresAt <= Date.now())) return false;
    activeMediaSignal = signal;
    pendingPlaybackMetadataProbe = null;
    playbackError = '';
    if (prepared?.trackId === id && prepared.url === url && prepared.resolution) {
      onlinePlayback.resolved = prepared.resolution;
      if (prepared.exactTrackId) pendingPlaybackMetadataProbe = { resolution: onlinePlayback.resolved, trackId: id, exactTrackId: prepared.exactTrackId, mediaUrl: url, signal };
    } else onlinePlayback.resolved = null;
    prefetchedPlayback = null;
    return true;
  },
  onPrefetchDiscarded: () => { prefetchedPlayback = null; },
  pause: () => { invalidateProtectedAudio(); audio.pause(); },
  setSource: (url) => {
    removeResumeSeek?.();
    removeResumeSeek = null;
    audio.pause();
    const currentTrack = trackById(state.currentId || '');
    const useLocalEq = equalizer.enabled && currentTrack?.sourceType === 'local' && url.startsWith(`blob:${location.origin}/`);
    const useRelayEq = equalizer.enabled && currentTrack?.sourceType === 'online' && onlinePlayback.resolved?.trackId === currentTrack.id && onlinePlayback.resolved.transport === 'relay';
    const useEq = useLocalEq || useRelayEq;
    let eqAttachFailed = false;
    if (isEqualizerBound(audio) || useEq) {
      const previous = audio;
      detachEqualizer(previous);
      let next = new Audio(); next.preload = 'metadata'; next.volume = state.volume; next.muted = ui.muted;
      if (useEq && !attachLocalEqualizer(next, url, useRelayEq)) {
        // Creating a media source is irreversible. A failed candidate is never reused.
        eqAttachFailed = true;
        releaseNativeMedia(next); next = new Audio(); next.preload = 'metadata'; next.volume = state.volume; next.muted = ui.muted;
      }
      nativeOutput.replaceStopped(next);
    }
    if (useEq && isEqualizerBound(audio)) setEqualizerOwner(audio); else if (!eqAttachFailed) markEqualizerBypass();
    audio.removeAttribute('src');
    audio.load();
    audio.src = url;
    audio.playbackRate = playbackIntent?.playbackRate || ui.playbackRate;
  },
  play: () => {
    const intent = playbackIntent;
    const signal = activeMediaSignal;
    const playing = intent?.preserveCurrentState && signal ? (async () => {
      await waitForMediaPosition(audio, () => intent.startPosition, signal);
      if (signal.aborted) throw new DOMException('播放请求已取消', 'AbortError');
      audio.playbackRate = intent.playbackRate || ui.playbackRate;
      if (intent.autoplay) {
        try { await awaitMediaOperation(audio.play(), signal); }
        catch (error) { if (signal.aborted || intent.autoplay || (error as Error)?.name !== 'AbortError') throw error; }
      }
      else audio.pause();
    })() : intent?.autoplay === false ? waitForAudioMetadata(signal) : audio.play();
    const probe = pendingPlaybackMetadataProbe;
    pendingPlaybackMetadataProbe = null;
    if (probe) void probePlaybackMetadata({
      ...probe, fetcher: apiFetch,
      current: () => ({ resolution: onlinePlayback.resolved, trackId: state.currentId, mediaUrl: activeMediaSignal === probe.signal ? audio.getAttribute('src') : null }),
      apply: metadata => { for (const [key, value] of Object.entries(metadata)) Vue.set(probe.resolution, key, value); },
    });
    return playing;
  },
  onCurrentChange: (id) => {
    if (qualitySwitchTask && qualitySwitchTask.trackId !== id) cancelQualitySwitch();
    if (onlinePlayback.busyTrackId !== id) onlinePlayback.busyTrackId = '';
    state.currentId = id;
    state.currentTime = 0;
    if (!id) {
      state.currentTime = 0;
      state.duration = 0;
      state.isPlaying = false;
      persist();
      render();
    } else {
      void loadTrackLyrics(id);
    }
    if (!audio.paused && !document.hidden) playbackController.prefetchNext();
  },
  onSelectionChange: (id) => {
    const track = trackById(id);
    if (!track) return;
    const preserveCurrentState = playbackIntent?.id === id && playbackIntent.preserveCurrentState === true;
    if (qualitySwitchTask && (!preserveCurrentState || qualitySwitchTask.trackId !== id)) cancelQualitySwitch();
    invalidateProtectedAudio();
    // Publish the requested identity before a remote resolver or CDN probe
    // completes. The Audio source remains untouched until onSourceReady.
    state.currentId = id;
    if (!preserveCurrentState) {
      state.currentTime = 0;
      state.duration = (track.durationMs || 0) / 1000;
      state.isPlaying = false;
      ui.positionRevision += 1;
    }
    void loadTrackLyrics(id);
    persist();
    render();
  },
  onSourceReady: (id, signal) => {
    const track = trackById(id);
    if (!track) return;
    // A saved position is used only by an explicit resume/source-switch request.
    // Selecting a song, including the current song, always starts from zero.
    const resumePosition = Math.max(0, Math.min(playbackIntent?.id === id ? playbackIntent.startPosition : 0, (track.durationMs || Infinity) / 1000));
    if (protectedAudioId) objectUrls.unprotect(protectedAudioId);
    protectedAudioId = id;
    objectUrls.protect(id);
    state.duration = (track.durationMs || 0) / 1000;
    state.currentTime = resumePosition;
    ui.positionRevision += 1;
    state.positions[id] = resumePosition;
    try { audio.currentTime = resumePosition; } catch { /* 元数据就绪前由 loadedmetadata 再次定位 */ }
    if (resumePosition > 0) {
      const media = audio;
      const restoreSeek = () => {
        if (media !== audio || state.currentId !== id || signal.aborted) return;
        const latestPosition = playbackIntent?.id === id && playbackIntent.preserveCurrentState ? playbackIntent.startPosition : resumePosition;
        try { media.currentTime = latestPosition; } catch { /* 当前媒体仍不可 seek */ }
      };
      media.addEventListener('loadedmetadata', restoreSeek, { once: true, signal });
      removeResumeSeek = () => media.removeEventListener('loadedmetadata', restoreSeek);
    }
    persist();
    render();
  },
  onUnavailable: (id) => {
    const track = trackById(id);
    const message = playbackError || (track?.sourceType === 'webdav' ? 'WebDAV 音频流获取失败，请检查连接和凭据' : track?.sourceType === 'online' ? '所选音源暂时无法提供有效音频，请切换音源或稍后重试' : '浏览器中没有这首歌曲的音频，请在本地音乐中重新选择原文件或原文件夹');
    rememberPlaybackFailure(id, 'resolve', message);
    notify(message, 'error');
  },
  onError: (id, error, stage) => {
    state.isPlaying = false;
    if (qualitySwitchTask?.trackId === id && qualitySwitchTask.phase === 'handoff') return;
    if (stage === 'play' && shouldRelayNativePlayback(onlinePlayback.resolved, id, error, audio.error?.code)) return;
    if ((error as DOMException)?.name !== 'AbortError') {
      const message = playbackError || (stage === 'resolve' ? sourceErrorText(error) : '浏览器阻止了自动播放或音频解码失败，请再次点击播放');
      rememberPlaybackFailure(id, stage, message);
      notify(message, stage === 'resolve' ? 'error' : 'info');
    }
    persist();
    render();
  },
});
const syncPlaybackController = () => {
  const snapshot = playbackController.snapshot();
  const list = activePlaybackList();
  if (!sameIds(snapshot.list, list)) playbackController.setList(list);
  if (!sameIds(snapshot.queue, state.queue)) playbackController.setQueue(state.queue);
  if (snapshot.mode !== state.playMode) playbackController.setMode(state.playMode);
  if (snapshot.currentId !== state.currentId) playbackController.setCurrent(state.currentId);
  const next = playbackController.snapshot();
  if (snapshot.list.join('\u0000') !== next.list.join('\u0000') || snapshot.queue.join('\u0000') !== next.queue.join('\u0000') || snapshot.playNext.join('\u0000') !== next.playNext.join('\u0000') || snapshot.currentId !== next.currentId || snapshot.mode !== next.mode) {
    playbackController.clearPrefetch();
    if (!audio.paused && !document.hidden) playbackController.prefetchNext();
  }
};
const onlinePlatform = (track: Track) => track.metadata?.platform === 'tx' || track.id.split(':')[2] === 'tx' ? 'tx' : 'wy';
const coverCandidate = (track?: Track) => {
  if (track?.sourceType === 'local' && track.availability === 'persistent') requestLocalPresentation(track.id);
  return trackArtworkCandidate(track, apiResourceUrl);
};
const cover = (track?: Track) => coverCandidate(track)
  || `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><rect width="400" height="400" fill="#d9dedb"/><circle cx="200" cy="200" r="122" fill="#202522"/><circle cx="200" cy="200" r="44" fill="#d9dedb"/><circle cx="200" cy="200" r="12" fill="#202522"/></svg>`)}`;
const effectiveLyrics = (track?: Track) => track?.lyrics?.length ? applyLyricOffset(track.lyrics, state.lyricOffsetMs) : [];
const activeLyricPosition = (track?: Track) => {
  const lines = track?.lyrics || [];
  return activeLyricIndex(lines, state.currentTime - state.lyricOffsetMs / 1000);
};
const activeLyric = (track?: Track) => {
  const lines = track?.lyrics || [];
  const index = activeLyricPosition(track);
  return index >= 0 ? lines[index] : undefined;
};
const effectiveAmllLyrics = (track?: Track): AmllLyricLine[] => {
  const source = track?.amllLyrics?.length ? track.amllLyrics : toAmllLines(track?.lyrics || []);
  const offset = state.lyricOffsetMs;
  return source.map((line) => ({
    ...line,
    translatedLyric: cleanAuxiliaryLyric(line.translatedLyric),
    romanLyric: cleanAuxiliaryLyric(line.romanLyric),
    startTime: Math.max(0, line.startTime + offset),
    endTime: Math.max(0, line.endTime + offset),
    words: line.words.map((word) => ({
      ...word,
      startTime: Math.max(0, word.startTime + offset),
      endTime: Math.max(0, word.endTime + offset),
    })),
  }));
};
const disposeAmllLyrics = () => {
  if (amllLyricFrame) window.cancelAnimationFrame(amllLyricFrame);
  amllLyricFrame = 0;
  amllLyricPlayer?.dispose();
  amllLyricPlayer = null;
};
const mountAmllLyrics = () => {
  disposeAmllLyrics();
  const host = $('[data-amll-host]') as HTMLElement | null;
  const track = currentTrack();
  if (!host || !track || !track.lyrics?.length) return;
  const lines = effectiveAmllLyrics(track);
  if (!lines.length) return;
  const player = new DomLyricPlayer();
  amllLyricPlayer = player;
  host.append(player.getElement());
  player.setLyricProcessConfig({ optimizeOptions: { normalizeSpaces: false } });
  player.setLyricLines(lines, Math.round(audio.currentTime * 1000));
  player.setCurrentTime(Math.round(audio.currentTime * 1000), true);
  if (state.isPlaying) player.resume(); else player.pause();
  player.update(0);
  const onLineClick = (event: Event) => {
    const index = Number((event as Event & { lineIndex?: number }).lineIndex);
    const line = Number.isInteger(index) ? lines[index] : undefined;
    if (!line) return;
    if (!seekAudio(line.startTime / 1000)) return;
    player.setCurrentTime(line.startTime, true);
  };
  player.addEventListener('line-click', onLineClick);
  let last = performance.now();
  const frame = (time: number) => {
    if (amllLyricPlayer !== player) return;
    const delta = Math.min(100, Math.max(0, time - last));
    last = time;
    player.setCurrentTime(Math.round(audio.currentTime * 1000));
    player.update(delta);
    amllLyricFrame = window.requestAnimationFrame(frame);
  };
  amllLyricFrame = window.requestAnimationFrame(frame);
};
const getMediaSession = () => typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : undefined;
const bindMediaSession = () => {
  const session = getMediaSession();
  if (!session || mediaSessionBound) return;
  mediaSessionBound = true;
  const setAction = (action: MediaSessionAction, handler: MediaSessionActionHandler) => { try { session.setActionHandler(action, handler); } catch { /* 浏览器可能只实现部分动作 */ } };
  setAction('play', () => togglePlay());
  setAction('pause', pauseCurrentPlayback);
  setAction('previoustrack', () => nextTrack(-1));
  setAction('nexttrack', () => nextTrack(1));
  setAction('seekbackward', (details) => { seekAudio(audio.currentTime - (details.seekOffset || 10)); });
  setAction('seekforward', (details) => { seekAudio(audio.currentTime + (details.seekOffset || 10)); });
  setAction('seekto', (details) => { if (details.seekTime !== undefined) seekAudio(details.seekTime); });
};
const syncMediaSession = () => {
  const session = getMediaSession();
  if (!session) return;
  bindMediaSession();
  const track = currentTrack();
  if (!track) { session.metadata = null; session.playbackState = 'none'; mediaMetadataKey = ''; return; }
  const artist = track.artists.join('、');
  // Use the same canonical image as the visible player. NetEase catalogues
  // can contain HTTP artworkRef URLs, which MediaMetadata also tries to load.
  const artwork = coverCandidate(track);
  const metadataKey = `${esc(track.id)}\u0000${track.title}\u0000${artist}\u0000${track.album}\u0000${artwork || ''}`;
  if (metadataKey !== mediaMetadataKey && typeof MediaMetadata !== 'undefined') {
    session.metadata = new MediaMetadata({ title: track.title, artist, album: track.album, artwork: artwork ? [{ src: artwork }] : [] });
    mediaMetadataKey = metadataKey;
  }
  session.playbackState = state.isPlaying ? 'playing' : 'paused';
  const duration = protectedAudioId === state.currentId && Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : state.duration;
  if (typeof session.setPositionState === 'function' && Number.isFinite(duration) && duration > 0) {
    const position = Math.min(Math.max(0, state.currentTime), duration);
    try { session.setPositionState({ duration, playbackRate: audio.playbackRate || 1, position }); } catch { /* duration may not be ready during source swap */ }
  }
};
const openAudioDb = () => new Promise<IDBDatabase>((resolve, reject) => { const request = indexedDB.open(AUDIO_DB, 1); request.onupgradeneeded = () => request.result.createObjectStore(AUDIO_STORE); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
const AUDIO_CACHE_CONFIG = '__mineradio_audio_cache_config__';
class AudioCacheLimitError extends Error { constructor(message: string) { super(message); this.name = 'AudioCacheLimitError'; } }
type AudioCacheConfig = { limit: number | null; autoCleanup?: boolean; usedAt: Record<string, number> };
const audioCache = Vue.observable({ bytes: 0, limit: null as number | null, autoCleanup: true, busy: false, error: '', revision: 0 });
const markEvictedAudio = (ids: string[], save = true) => {
  for (const id of ids) {
    const track = trackById(id);
    if (!track || track.sourceType !== 'local' || track.availability !== 'persistent') continue;
    const session = sessionFiles.has(id) || objectUrls.has(id);
    track.availability = session ? 'session' : 'unavailable';
    track.sourceId = session ? 'browser-session' : 'browser-indexeddb';
  }
  if (ids.length && save) persist();
};
// All size checks and deletions run inside one IDB write transaction, serialized across tabs.
const manageAudioCache = async (options: { limit?: number | null; autoCleanup?: boolean; file?: File; id?: string; touch?: string; clear?: boolean; readOnly?: boolean; deferRefresh?: boolean } = {}) => {
  const db = await openAudioDb();
  const evicted: string[] = [];
  let bytes = 0, limit: number | null = null, autoCleanup = true, rejected = false;
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, options.readOnly ? 'readonly' : 'readwrite');
      const store = tx.objectStore(AUDIO_STORE);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error || (rejected ? new AudioCacheLimitError('音频缓存空间不足，仅在当前会话可用') : new Error('音频缓存事务失败')));
      tx.onerror = () => reject(tx.error || new Error('音频缓存操作失败'));
      const request = store.get(AUDIO_CACHE_CONFIG);
      request.onsuccess = () => {
        const saved = request.result as AudioCacheConfig | undefined;
        const config: AudioCacheConfig = { limit: options.limit !== undefined ? options.limit : saved?.limit ?? null, autoCleanup: options.autoCleanup !== undefined ? options.autoCleanup : saved?.autoCleanup !== false, usedAt: { ...(saved?.usedAt || {}) } };
        limit = config.limit;
        autoCleanup = config.autoCleanup !== false;
        if (options.touch) { if (saved && !options.readOnly) { config.usedAt[options.touch] = Date.now(); store.put(config, AUDIO_CACHE_CONFIG); } return; }
        // Unlimited imports retain the inexpensive existing write path.
        if (options.file && options.id && limit === null && options.limit === undefined) { store.put(options.file, options.id); return; }
        const entries: Array<{ id: string; size: number; used: number }> = [];
        const cursor = store.openCursor();
        cursor.onsuccess = () => {
          const item = cursor.result;
          if (item) {
            if (item.value instanceof Blob && typeof item.key === 'string') {
              const id = item.key;
              if (id !== options.id || !options.file || (limit !== null && options.file.size > limit)) entries.push({ id, size: item.value.size, used: config.usedAt[id] || state.historyPlayedAt[id] || Date.parse(trackById(id)?.createdAt || '') || 0 });
            }
            item.continue(); return;
          }
          if (options.file && options.id) {
            if (limit !== null && options.file.size > limit) rejected = true;
            else { store.put(options.file, options.id); config.usedAt[options.id] = Date.now(); entries.push({ id: options.id, size: options.file.size, used: config.usedAt[options.id] }); }
          }
          bytes = entries.reduce((sum, entry) => sum + entry.size, 0);
          if (!options.readOnly) {
            const protectedIds = new Set([state.currentId, protectedAudioId, playbackIntent?.id, options.id]);
            entries.sort ( (a, b) => a.used - b.used );
            for (const entry of entries) {
              if (!options.clear && (!autoCleanup || limit === null || bytes <= limit)) break;
              if (!options.clear && protectedIds.has(entry.id)) continue;
              store.delete(entry.id); delete config.usedAt[entry.id]; bytes -= entry.size; evicted.push(entry.id);
            }
            // A currently playing file cannot be evicted to make room. Roll
            // back the whole import rather than silently exceeding the limit.
            if (options.file && limit !== null && bytes > limit) { rejected = true; tx.abort(); return; }
            if (saved || options.limit !== undefined || options.autoCleanup !== undefined) store.put(config, AUDIO_CACHE_CONFIG);
          }
        };
      };
    });
  } finally { db.close(); }
  if (!options.touch && (options.readOnly || options.file || options.clear || evicted.length)) audioCache.revision++;
  // The unlimited import fast path intentionally avoids scanning every blob,
  // but the settings page still promises an accurate occupied-size value.
  // Batch imports refresh once at completion rather than scanning all stored
  // blobs after every file. Single-file restores still refresh immediately.
  if (!options.touch && options.file && limit === null && !options.deferRefresh) await refreshAudioCache();
  else if (options.file && limit === null && options.deferRefresh) { /* refreshed by the import's finally block */ }
  else if (!options.touch) { audioCache.bytes = bytes; audioCache.limit = limit; audioCache.autoCleanup = autoCleanup; }
  markEvictedAudio(evicted);
  if (evicted.length || options.limit !== undefined || options.autoCleanup !== undefined || options.clear || (options.file && !options.deferRefresh && !rejected)) {
    try { localStorage.setItem('mineradio.audio-cache-changed', String(Date.now()) + Math.random()); } catch { /* optional cross-tab notification */ }
  }
  if (rejected) throw new AudioCacheLimitError('音频文件超过缓存上限，仅在当前会话可用');
};
const refreshAudioCache = async (notifyTabs = false) => { audioCache.error = ''; try { await manageAudioCache({ readOnly: true }); } catch (error) { audioCache.error = audioCacheErrorText(error); } if (notifyTabs) { try { localStorage.setItem('mineradio.audio-cache-changed', String(Date.now()) + Math.random()); } catch { /* optional cross-tab notification */ } } };
const setAudioCacheLimit = async (limit: number | null) => { if (limit !== null && (!Number.isSafeInteger(limit) || limit < 0)) throw new Error('请输入有效的缓存上限'); await manageAudioCache({ limit }); };
const setAudioCacheAutoCleanup = async (enabled: boolean) => { if (typeof enabled !== 'boolean') throw new Error('请选择有效的自动清理设置'); await manageAudioCache({ autoCleanup: enabled }); };
window.addEventListener('storage', event => {
  if (event.key !== 'mineradio.audio-cache-changed') return;
  void (async () => {
    await refreshAudioCache();
    try {
      const db = await openAudioDb();
      const keys = await new Promise<IDBValidKey[]>((resolve, reject) => { const request = db.transaction(AUDIO_STORE, 'readonly').objectStore(AUDIO_STORE).getAllKeys(); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
      db.close();
      const present = new Set(keys);
      markEvictedAudio(state.tracks.filter(track => track.sourceType === 'local' && track.availability === 'persistent' && !present.has(track.id)).map(track => track.id), false);
    } catch { /* Leave availability unchanged if storage is inaccessible. */ }
  })();
});
void refreshAudioCache();
const storeAudioBlob = async (id: string, file: File, deferRefresh = false) => manageAudioCache({ id, file, deferRefresh });
const loadAudioBlob = async (id: string) => { const db = await openAudioDb(); const file = await new Promise<Blob | undefined>((resolve, reject) => { const request = db.transaction(AUDIO_STORE, 'readonly').objectStore(AUDIO_STORE).get(id); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); }); db.close(); return file; };
async function readAudioCacheStatus(ids: string[]): Promise<Record<string, boolean>> {
  const keys = [...new Set(ids)];
  const db = await openAudioDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(AUDIO_STORE, 'readonly'), store = tx.objectStore(AUDIO_STORE);
      const result: Record<string, boolean> = {};
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error || new Error('无法读取音频缓存'));
      tx.onabort = () => reject(tx.error || new Error('读取音频缓存已取消'));
      for (const id of keys) { const request = store.get(id); request.onsuccess = () => { result[id] = request.result instanceof Blob && request.result.size > 0; }; }
    });
  } finally { db.close(); }
}
const deleteAudioBlob = async (id: string): Promise<boolean> => {
  try {
    const db = await openAudioDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(AUDIO_STORE, 'readwrite'), store = tx.objectStore(AUDIO_STORE);
        store.delete(id);
        const request = store.get(AUDIO_CACHE_CONFIG);
        request.onsuccess = () => { const config = request.result as AudioCacheConfig | undefined; if (config?.usedAt) { delete config.usedAt[id]; store.put(config, AUDIO_CACHE_CONFIG); } };
        tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error || new Error('删除缓存失败')); tx.onerror = () => reject(tx.error || new Error('删除缓存失败'));
      });
    } finally { db.close(); }
    await refreshAudioCache();
    try { localStorage.setItem('mineradio.audio-cache-changed', String(Date.now()) + Math.random()); } catch { /* optional cross-tab notification */ }
    return true;
  } catch { return false; }
};
const clearAudioCache = async () => {
  await manageAudioCache({ clear: true });
  for (const track of state.tracks) if (track.sourceType === 'local' && track.availability === 'persistent') {
    const availableThisSession = sessionFiles.has(track.id) || objectUrls.has(track.id);
    track.availability = availableThisSession ? 'session' : 'unavailable';
    track.sourceId = availableThisSession ? 'browser-session' : 'browser-indexeddb';
  }
  persist();
  notify('音频缓存已清除，歌曲索引与原始文件仍保留', 'success');
};
const clearTrackAudioCache = async (id: string) => {
  const track = trackById(id);
  if (!track) { notify('没有找到要清理的歌曲', 'error'); return false; }
  if (track.sourceType !== 'local' || track.availability !== 'persistent') {
    notify('这首歌没有保存在浏览器中的音频缓存', 'info');
    return false;
  }
  if (!await deleteAudioBlob(id)) { notify('清除音频缓存失败，请稍后重试', 'error'); return false; }
  const availableThisSession = sessionFiles.has(id) || objectUrls.has(id);
  track.availability = availableThisSession ? 'session' : 'unavailable';
  track.sourceId = availableThisSession ? 'browser-session' : 'browser-indexeddb';
  track.updatedAt = new Date().toISOString();
  persist();
  notify(`已清除「${track.title}」的音频缓存，歌曲索引仍保留`, 'success');
  return true;
};
const emptyEmbeddedLyrics = (): EmbeddedLyrics => ({ lyrics: [], amllLyrics: [], warnings: [], sources: [] });
const localPresentations = new Map<string, LocalPresentation>();
// A cached object is safe to omit from the index only after an IDB save/readback
// succeeds. An in-memory entry or a pending/failed write is not durable proof.
const persistedLocalPresentations = new Map<string, LocalPresentation>();
const presentationPending = new Set<string>(), presentationAttempted = new Set<string>();
const presentationRequests = new Map<string, { promise: Promise<void>; resolve: () => void }>();
let presentationHydrating = true, presentationActive = 0, presentationTimer = 0, presentationSaveTimer = 0;
const finishLocalPresentation = (id: string) => {
  const request = presentationRequests.get(id);
  if (request) { presentationRequests.delete(id); request.resolve(); }
};
const presentationMatches = (track: Track, cached: LocalPresentation) => cached.version === LOCAL_PRESENTATION_VERSION && cached.fingerprint === (track.fingerprint || '') && cached.size === track.size;
const presentationNeedsLyricRepair = (cached: LocalPresentation) => Boolean(cached.lyrics.plainText?.trim() && hasMisreadLyricist(cached.lyrics.sources));
const presentationNeedsRefresh = (cached: LocalPresentation, track?: Track) => cached.status === 'failed' || presentationNeedsLyricRepair(cached) || (
  // Earlier readers treated hour-based LRC tags as plain text. Repair only
  // the requested original entry, preserving manual/sidecar timelines.
  !cached.lyricOverride && !cached.lyrics.lyrics.length && !cached.lyrics.amllLyrics.length &&
  !track?.lyrics?.length && !track?.amllLyrics?.length &&
  /\[\d{1,2}:[0-5]\d:[0-5]\d(?:[.:]\d{1,3})?\]/.test(cached.lyrics.plainText || '')
) || (
  // Older MP3 cache misses may have skipped a native ID3v2.2 ULT frame.
  // Repair only requested empty entries; keep their cover and do not scan
  // the rest of the library or replace existing sidecar/manual lyrics.
  cached.id3v22LyricsChecked !== true && /\.mp3$/i.test(track?.fileName || track?.externalId || '') &&
  !cached.lyrics.lyrics.length && !cached.lyrics.amllLyrics.length && !cached.lyrics.plainText?.trim() &&
  !track?.lyrics?.length && !track?.amllLyrics?.length && !track?.metadata?.embeddedPlainLyrics?.trim()
);
const schedulePresentationSave = () => {
  if (presentationSaveTimer) return;
  presentationSaveTimer = window.setTimeout(() => { presentationSaveTimer = 0; persistIfStorageUnchanged(); }, 250);
};
const applyLocalPresentation = (track: Track, cached: LocalPresentation) => {
  if (!presentationMatches(track, cached)) return;
  const needsRepair = presentationNeedsLyricRepair(cached);
  if (!presentationNeedsRefresh(cached, track)) presentationPending.delete(track.id);
  const lyrics = needsRepair ? { ...cached.lyrics, plainText: undefined, sources: cached.lyrics.sources.filter(source => !/(?:^|:)lyricist$/i.test(source.tag)) } : cached.lyrics;
  Vue.set(track, 'metadata', localPresentationMetadata(track.metadata, lyrics, cached));
  // Sidecar/manual lyrics already saved in the library always take priority.
  if (track.metadata?.localIndexLyricsOverride !== true && !track.lyrics?.length && !track.amllLyrics?.length && cached.lyrics.lyrics.length) {
    Vue.set(track, 'lyrics', cached.lyrics.lyrics); Vue.set(track, 'amllLyrics', cached.lyrics.amllLyrics);
    Vue.set(state.lyrics, track.id, cached.lyrics.lyrics);
  }
  if (cached.artwork?.size && !artworkUrls.has(track.id)) {
    const url = URL.createObjectURL(cached.artwork); artworkUrls.set(track.id, url); Vue.set(track, 'artworkRef', url);
  }
  if (!presentationNeedsRefresh(cached, track)) finishLocalPresentation(track.id);
};
const latestLocalPresentationLyrics = (track: Track, fallback: EmbeddedLyrics) => {
  const entry = localPresentations.get(track.id);
  const latest = entry && presentationMatches(track, entry) ? entry : undefined;
  // Read after the final parser/image/audio await: a manual binding can have
  // replaced the cache while recovery was reading the original file.
  const currentOverride = Boolean(track.lyrics?.length || track.amllLyrics?.length) &&
    (!latest || track.lyrics !== latest.lyrics.lyrics || track.amllLyrics !== latest.lyrics.amllLyrics);
  return {
    lyrics: currentOverride ? { ...(latest?.lyrics || fallback), lyrics: track.lyrics || [], amllLyrics: track.amllLyrics || [] }
      : latest?.lyricOverride ? latest.lyrics : fallback,
    lyricOverride: currentOverride || latest?.lyricOverride === true,
  };
};
const migrateLocalPresentation = async (id: string) => {
  const track = trackById(id);
  if (!track || track.sourceType !== 'local' || track.availability !== 'persistent') return;
  const fingerprint = track.fingerprint || '', size = track.size;
  const previous = localPresentations.get(id);
  let artwork = previous && presentationMatches(track, previous) ? previous.artwork : undefined;
  let lyrics = emptyEmbeddedLyrics(), status: LocalPresentation['status'] = 'ready';
  try {
    const file = await loadAudioBlob(id); if (!file) return;
    const parsed = await parseBlob(file, { duration: false });
    lyrics = readEmbeddedLyrics(parsed, track.durationMs);
    const picture = parsed.common.picture ? selectCover(parsed.common.picture) : undefined;
    if (picture?.data.byteLength) artwork = await thumbnailArtwork(picture);
  } catch { status = 'failed'; lyrics.warnings.push('无法读取此文件的内嵌封面或歌词；未修改原始音频。'); }
  const current = trackById(id);
  if (current !== track || (current.fingerprint || '') !== fingerprint || current.size !== size || current.availability !== 'persistent') return;
  const latestLyrics = latestLocalPresentationLyrics(current, lyrics);
  const cached: LocalPresentation = { id, version: LOCAL_PRESENTATION_VERSION, fingerprint, size, artwork, embeddedCover: Boolean(artwork?.size), ...latestLyrics, id3v22LyricsChecked: true, status, updatedAt: new Date().toISOString() };
  localPresentations.set(id, cached); applyLocalPresentation(current, cached); schedulePresentationSave();
  await saveLocalPresentation(current, cached);
};
const drainLocalPresentations = () => {
  presentationTimer = 0;
  if (presentationHydrating || !presentationPending.size) return;
  while (presentationActive < 2 && presentationPending.size) {
    // Rendered tracks request their own metadata; the current song jumps ahead
    // of the virtual-list overscan. Do not scan the whole audio library.
    const id = state.currentId && presentationPending.has(state.currentId) ? state.currentId : presentationPending.values().next().value!;
    presentationPending.delete(id);
    const track = trackById(id), cached = localPresentations.get(id);
    // cover() can queue while bootstrap is still restoring the small cache.
    // A cache hit must cancel that old request before any full-file read.
    if (track && cached && presentationMatches(track, cached) && !presentationNeedsRefresh(cached, track)) { finishLocalPresentation(id); continue; }
    presentationAttempted.add(id); presentationActive++;
    void migrateLocalPresentation(id).finally(() => { finishLocalPresentation(id); presentationActive--; if (!presentationTimer) presentationTimer = window.setTimeout(drainLocalPresentations, 0); });
  }
};
function requestLocalPresentation(id: string): Promise<void> {
  const track = trackById(id); if (!track || track.sourceType !== 'local' || track.availability !== 'persistent') return Promise.resolve();
  const cached = localPresentations.get(id);
  if (cached && presentationMatches(track, cached) && !presentationNeedsRefresh(cached, track)) return Promise.resolve();
  const pending = presentationRequests.get(id); if (pending) return pending.promise;
  if (presentationAttempted.has(id)) return Promise.resolve();
  let resolve!: () => void;
  const promise = new Promise<void>(done => { resolve = done; });
  presentationRequests.set(id, { promise, resolve });
  presentationPending.add(id);
  if (!presentationHydrating && !presentationTimer) presentationTimer = window.setTimeout(drainLocalPresentations, 0);
  return promise;
}
const hydratePersistentTracks = async () => {
  const local = state.tracks.filter(track => track.sourceType === 'local');
  const restored = local.filter(track => track.availability === 'persistent');
  try {
    await Promise.all([
      (async () => {
        // Small covers/tags survive session-only audio and an unavailable audio
        // database. Never read full audio files to restore these cached records.
        try {
          const cached = await readLocalPresentations(new Set(local.map(track => track.id)));
          for (const track of local) { const entry = cached.get(track.id); if (entry && trackById(track.id) === track && presentationMatches(track, entry)) { persistedLocalPresentations.set(track.id, entry); localPresentations.set(track.id, entry); applyLocalPresentation(track, entry); } }
        } catch { /* Presentation storage and audio availability are independent. */ }
      })(),
      (async () => {
        if (!restored.length) return;
        try {
          const db = await openAudioDb();
          let keys: IDBValidKey[];
          try { keys = await new Promise<IDBValidKey[]>((resolve, reject) => { const request = db.transaction(AUDIO_STORE, 'readonly').objectStore(AUDIO_STORE).getAllKeys(); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); }); }
          finally { db.close(); }
          const storedIds = new Set(keys);
          for (const track of restored) if (!storedIds.has(track.id)) track.availability = 'unavailable';
        } catch { for (const track of restored) track.availability = 'unavailable'; }
      })(),
    ]);
  }
  finally { presentationHydrating = false; if (state.currentId) requestLocalPresentation(state.currentId); drainLocalPresentations(); persistIfStorageUnchanged(); render(); }
};
const saveLocalPresentation = async (track: Track, cached: LocalPresentation) => {
  try {
    await storeLocalPresentation(cached);
    persistedLocalPresentations.set(track.id, cached);
    if (track.metadata?.localPresentationCacheError) Vue.delete(track.metadata, 'localPresentationCacheError');
  } catch {
    Vue.set(track, 'metadata', { ...track.metadata, localPresentationCacheError: '封面与歌词缓存保存失败，刷新后可能需重新选择原文件；音频导入不受影响。' });
  }
};
const readableBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
};
const yieldToBrowser = () => new Promise<void>((resolve) => window.setTimeout(resolve, 0));
const throwIfAborted = (signal: AbortSignal) => { if (signal.aborted) throw new DOMException('导入已取消', 'AbortError'); };
const probeDuration = (url: string, signal: AbortSignal) => new Promise<number | undefined>((resolve, reject) => {
  const probe = document.createElement('audio');
  let settled = false;
  const finish = (duration?: number) => { if (settled) return; settled = true; window.clearTimeout(timeout); signal.removeEventListener('abort', onAbort); probe.removeAttribute('src'); probe.load(); resolve(duration); };
  const onAbort = () => { if (settled) return; settled = true; window.clearTimeout(timeout); signal.removeEventListener('abort', onAbort); probe.removeAttribute('src'); probe.load(); reject(new DOMException('导入已取消', 'AbortError')); };
  const timeout = window.setTimeout(() => finish(undefined), 10000);
  probe.preload = 'metadata'; probe.onloadedmetadata = () => finish(Number.isFinite(probe.duration) ? probe.duration * 1000 : undefined); probe.onerror = () => finish(undefined); signal.addEventListener('abort', onAbort, { once: true }); probe.src = url;
});
const parseBlobWithAbort = async (file: File, signal: AbortSignal) => {
  throwIfAborted(signal);
  let onAbort: (() => void) | undefined;
  try {
    // Small files avoid repeated asynchronous Blob reads inside the tokenizer.
    // Keep a strict per-file memory bound and random access for large audio.
    const parsing = file.size <= 16 * 1024 * 1024 ? (async () => {
      const bytes = await file.arrayBuffer();
      throwIfAborted(signal);
      return parseBuffer(new Uint8Array(bytes), { mimeType: file.type, size: file.size });
    })() : parseBlob(file);
    return await Promise.race([parsing, new Promise<never>((_, reject) => {
      onAbort = () => reject(new DOMException('导入已取消', 'AbortError'));
      signal.addEventListener('abort', onAbort, { once: true });
    })]);
  } finally { if (onAbort) signal.removeEventListener('abort', onAbort); }
};

function setTheme(theme: SavedState['theme']) { state.theme = theme; document.documentElement.dataset.theme = theme === 'system' ? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme; persist(); render(); }
function setLyricOffset(value: number, rerender = true) {
  state.lyricOffsetMs = Math.max(-5000, Math.min(5000, Math.round(value / 100) * 100));
  persist();
  if (rerender) render();
}
function adjustLyricOffset(deltaMs: number) {
  setLyricOffset(state.lyricOffsetMs + deltaMs);
}
function setPlaybackRate(value: number) {
  if (!Number.isFinite(value)) return;
  const rate = Math.max(0.5, Math.min(2, Math.round(Number(value) * 20) / 20));
  audio.defaultPlaybackRate = rate;
  audio.playbackRate = rate;
  if (qualitySwitchTask?.intent) qualitySwitchTask.intent.playbackRate = rate;
  ui.playbackRate = rate;
  try { localStorage.setItem(PLAYBACK_RATE_KEY, String(rate)); } catch { /* 当前页面仍可使用 */ }
  syncMediaSession();
  render();
}
function setMotionLevel(level: MotionLevel) { state.motionLevel = level; persist(); render(); }
function refreshLyricDom() {
  const track = currentTrack();
  const lines = effectiveLyrics(track);
  const index = activeLyricIndex(lines, state.currentTime);
  document.querySelectorAll<HTMLElement>('.lyric-line').forEach((element, lineIndex) => {
    element.classList.toggle('is-active', lineIndex === index);
    element.dataset.seek = String(lines[lineIndex]?.time ?? 0);
  });
  renderedLyricIndex = index;
  const output = $('[data-lyric-offset-value]');
  if (output) output.textContent = `${state.lyricOffsetMs > 0 ? '+' : ''}${state.lyricOffsetMs} ms`;
}
function syncLyricActiveLine() {
  const track = currentTrack();
  const index = activeLyricPosition(track);
  if (index === renderedLyricIndex) return;
  const previous = document.querySelector('.lyric-line.is-active');
  previous?.classList.remove('is-active');
  const next = index >= 0 ? document.querySelectorAll<HTMLElement>('.lyric-line')[index] : undefined;
  next?.classList.add('is-active');
  renderedLyricIndex = index;
  if (next && !matchMedia('(prefers-reduced-motion: reduce)').matches && state.motionLevel !== 'off') next.scrollIntoView({ block: 'center', behavior: 'smooth' });
}
function applyImmersiveBackdrop() {
  const backdrop = $('.immersive-backdrop') as HTMLElement | null;
  const track = currentTrack();
  if (backdrop && track) backdrop.style.backgroundImage = `url("${cover(track).replace(/"/g, '\\"')}")`;
}
function removeTrack(id: string, deleteCache = false) {
  const track = trackById(id);
  if (!track) { notify('没有找到要移除的歌曲', 'error'); return; }
  if (artworkUrls.has(id)) URL.revokeObjectURL(artworkUrls.get(id)!);
  if (deleteCache) void deleteAudioBlob(id);
  objectUrls.revoke(id); sessionFiles.delete(id); artworkUrls.delete(id);
  delete state.positions[id]; delete state.lyrics[id];
  state.tracks = state.tracks.filter((item) => item.id !== id);
  pruneTrackReferences();
  state.queue = state.queue.filter((trackId) => trackId !== id);
  state.favorites = state.favorites.filter((trackId) => trackId !== id);
  state.playlists.forEach((playlist) => playlist.trackIds = playlist.trackIds.filter((trackId) => trackId !== id));
  if (state.currentId === id) {
    cancelPlaybackRequest(); audio.pause(); audio.removeAttribute('src'); audio.load();
    state.currentId = null; state.isPlaying = false; state.currentTime = 0; state.duration = 0;
  }
  persist();
  notify(deleteCache ? `已移除「${track.title}」的索引和音频缓存` : `已移除「${track.title}」的歌曲索引，浏览器音频缓存未删除`, 'success');
}
function updateTrack(id: string, fields: Partial<Track>) {
  const track = trackById(id);
  if (!track) { notify('没有找到要编辑的歌曲', 'error'); return false; }
  const capability = trackMetadataEditCapability(track);
  if (!capability.editable) { notify(capability.reason, 'info'); return false; }
  const saveError = '歌曲索引无法读取或保存，修改未提交；请刷新页面或释放站点存储空间后重试。';
  const conflict = () => new Error('另一标签已修改或删除这首歌曲，修改未提交；请刷新页面后重试。');
  const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
  try {
    const snapshot = localStorage.getItem(STORAGE_KEY);
    const latest: SavedState = JSON.parse(snapshot || 'null');
    if (!Array.isArray(latest?.tracks)) throw new Error(saveError);
    const shared = latest.tracks.find(item => item?.id === id);
    if (!shared || (['sourceType', 'sourceId', 'externalId', 'fingerprint', 'size', 'createdAt'] as const).some(key => !same(track[key], shared[key])) || !same(track.metadata?.localFileSampleSignature, shared.metadata?.localFileSampleSignature)) throw conflict();
    const keys = (['title', 'artists', 'album'] as const).filter(key => fields[key] !== undefined && !same(track[key], fields[key]));
    for (const key of keys) if (!same(track[key], shared[key]) && !same(fields[key], shared[key])) throw conflict();
    const updated = { ...shared, updatedAt: new Date().toISOString() };
    for (const key of keys) Vue.set(updated, key, fields[key]);
    const serialized = JSON.stringify({ ...latest, tracks: latest.tracks.map(item => item === shared ? updated : item) });
    if (localStorage.getItem(STORAGE_KEY) !== snapshot) throw conflict();
    localStorage.setItem(STORAGE_KEY, serialized);
    for (const key of keys) Vue.set(track, key, fields[key]);
    track.updatedAt = updated.updatedAt;
    if (lastFullStateSnapshot === snapshot) lastFullStateSnapshot = serialized;
  } catch (error) {
    notify(error instanceof Error && error.message.startsWith('另一标签') ? error.message : saveError, 'error');
    return false;
  }
  notify('歌曲信息已保存', 'success');
  return true;
}
async function updateLocalTrackDetails(id: string, input: { title: string; artists: string[]; album: string; coverDataUrl?: string; lyricsFile?: File }): Promise<boolean> {
  const original = trackById(id);
  const capability = trackMetadataEditCapability(original);
  if (!original || !capability.editable) { notify(capability.reason || '没有找到要编辑的歌曲', 'error'); return false; }
  const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
  const identity = (track: Track) => [track.sourceType, track.sourceId, track.externalId, track.fingerprint, track.size, track.createdAt, track.metadata?.localFileSampleSignature];
  const initialIdentity = identity(original), initial = { title: original.title, artists: [...original.artists], album: original.album, customCover: original.customCover, lyrics: JSON.stringify([original.lyrics, original.amllLyrics, original.metadata?.localIndexLyricsOverride]) };
  const conflict = () => new Error('歌曲已在其他页面修改、移除或更换原文件，修改未提交；请重新打开编辑。');
  const readSnapshot = () => {
    const snapshot = localStorage.getItem(STORAGE_KEY);
    const saved: SavedState = JSON.parse(snapshot || 'null');
    if (!Array.isArray(saved?.tracks)) throw new Error('无法读取音乐库索引，修改未提交。');
    const track = saved.tracks.find(item => item?.id === id);
    if (!track || !same(identity(track), initialIdentity)) throw conflict();
    return { snapshot, saved, track };
  };
  try {
    const title = typeof input.title === 'string' ? input.title.trim() : '';
    const album = typeof input.album === 'string' ? input.album.trim() || '未知专辑' : '';
    if (!title || title.length > 300 || !album || album.length > 300 || !Array.isArray(input.artists) || input.artists.some(artist => typeof artist !== 'string' || artist.length > 500)) throw new Error('请填写有效的歌曲名称、艺术家和专辑。');
    const artists = input.artists.map(artist => artist.trim()).filter(Boolean);
    if (artists.length > 32 || artists.join(' / ').length > 500) throw new Error('艺术家信息过长。');
    if (!artists.length) artists.push('未知艺术家');
    const beginning = readSnapshot();
    const coverDataUrl = input.coverDataUrl;
    if (coverDataUrl !== undefined) await verifyPlaylistCover(coverDataUrl);
    let parsedLyrics: { lyrics: LyricLine[]; amllLyrics: AmllLyricLine[]; fileName: string } | undefined;
    if (input.lyricsFile !== undefined) {
      const file = input.lyricsFile;
      if (!isLyricImport(file) || file.size > 8 * 1024 * 1024) throw new Error('请选择不超过 8 MiB 的歌词文件。');
      const text = await readLyricText(file), lyrics = parseTimedLyrics(file.name, text);
      if (!lyrics.length) throw new Error('歌词文件中没有可识别的时间轴。');
      const amll = parseAmllLyrics(file.name, text, lyrics);
      if (lyrics.length > 20000 || lyrics.some(line => line.text.length > 8192) || amll.reduce((total, line) => total + line.words.length, 0) > 100000) throw new Error('歌词内容超过可保存的行数或文字限制。');
      parsedLyrics = { lyrics, amllLyrics: amll.length ? amll : toAmllLines(lyrics), fileName: file.name };
    }
    if (trackById(id) !== original || !same(identity(original), initialIdentity)) throw conflict();
    const latest = readSnapshot(), changes: Partial<Track> = {}, fields = { title, artists, album };
    for (const key of ['title', 'artists', 'album'] as const) {
      if (same(fields[key], initial[key])) continue;
      if (!same(original[key], initial[key]) || !same(latest.track[key], initial[key]) && !same(latest.track[key], fields[key])) throw conflict();
      Vue.set(changes, key, fields[key]);
    }
    if (coverDataUrl !== undefined && coverDataUrl !== initial.customCover) {
      if (original.customCover !== initial.customCover || latest.track.customCover !== initial.customCover && latest.track.customCover !== coverDataUrl) throw conflict();
      changes.customCover = coverDataUrl;
    }
    if (parsedLyrics) {
      if (latest.track.updatedAt !== beginning.track.updatedAt || JSON.stringify([original.lyrics, original.amllLyrics, original.metadata?.localIndexLyricsOverride]) !== initial.lyrics) throw conflict();
      changes.lyrics = parsedLyrics.lyrics; changes.amllLyrics = parsedLyrics.amllLyrics;
      changes.metadata = { ...latest.track.metadata, localIndexLyricsOverride: true, localIndexLyricsFileName: parsedLyrics.fileName };
    }
    const updatedAt = new Date().toISOString();
    const updated = { ...latest.track, ...changes, updatedAt };
    const serialized = JSON.stringify({ ...latest.saved, tracks: latest.saved.tracks.map(track => track === latest.track ? updated : track), ...(parsedLyrics ? { lyrics: { ...latest.saved.lyrics, [id]: parsedLyrics.lyrics } } : {}) });
    if (localStorage.getItem(STORAGE_KEY) !== latest.snapshot) throw conflict();
    localStorage.setItem(STORAGE_KEY, serialized);
    for (const [key, value] of Object.entries(changes)) Vue.set(original, key, value);
    original.updatedAt = updatedAt;
    if (parsedLyrics) Vue.set(state.lyrics, id, parsedLyrics.lyrics);
    if (lastFullStateSnapshot === latest.snapshot) lastFullStateSnapshot = serialized;
    try { if (state.currentId === id) { syncMediaSession(); if (ui.lyricsOpen) Vue.nextTick(() => { if (state.currentId === id) mountAmllLyrics(); }); } } catch { /* Index commit remains valid when optional presentation is unavailable. */ }
    notify('歌曲资料已保存', 'success');
    return true;
  } catch (error) { notify(isQuotaExceededError(error) ? '浏览器空间不足，修改未提交；原资料已保留。' : (error as Error).message || '歌曲资料无法保存，修改未提交。', 'error'); return false; }
}
async function bindLyrics(id: string, file: File): Promise<boolean> {
  const track = trackById(id);
  if (!track) { notify('没有找到要关联歌词的歌曲', 'error'); return false; }
  if (!isLyricImport(file)) { notify('请选择支持的歌词文件', 'error'); return false; }
  try {
    const text = await readLyricText(file);
    const lyrics = parseTimedLyrics(file.name, text);
    if (!lyrics.length) throw new Error('没有可识别的时间轴');
    const amllLyrics = parseAmllLyrics(file.name, text, lyrics);
    Vue.set(track, 'lyrics', lyrics);
    Vue.set(track, 'amllLyrics', amllLyrics.length ? amllLyrics : undefined);
    Vue.set(state.lyrics, id, lyrics);
    track.updatedAt = new Date().toISOString();
    await cacheLocalLyricOverride(track);
    if (!persist()) return false;
    notify(`已关联 ${lyrics.length} 行歌词`, 'success');
    return true;
  } catch (error) {
    notify(`关联歌词失败：${error instanceof Error ? error.message : '无法读取歌词'}`, 'error');
    return false;
  }
}
const cacheLocalLyricOverride = async (track: Track) => {
  if (track.sourceType !== 'local') return;
  const previous = localPresentations.get(track.id);
  if (!previous || !presentationMatches(track, previous)) return;
  // Manual/sidecar timelines remain the primary lyrics after a metadata repair
  // or file restore. Only successful IDB saves may compact them from the index.
  const cached: LocalPresentation = { ...previous, lyricOverride: true, lyrics: { ...previous.lyrics, lyrics: track.lyrics || [], amllLyrics: track.amllLyrics || [] }, updatedAt: track.updatedAt };
  localPresentations.set(track.id, cached);
  await saveLocalPresentation(track, cached);
};
async function importFiles(files: FileList | File[], persistent = false, playlistId?: string) {
  if (activeImportAbort) return notify('已有导入任务正在进行，请先等待或取消当前任务', 'info');
  const list = [...files]; const audioFiles = list.filter(isAudioImport); const lyricFiles = list.filter(isLyricImport);
  if (!audioFiles.length && !lyricFiles.length) return notify('没有识别到可导入的音频或歌词文件', 'error');
  const playlist = playlistId ? state.playlists.find(item => item.id === playlistId) : undefined;
  if (playlistId && !playlist) throw new Error('目标歌单不存在');
  const controller = new AbortController(); activeImportAbort = controller;
  const task: ImportTask = { status: 'running', total: audioFiles.length + lyricFiles.length, processed: 0, audioTotal: audioFiles.length, lyricTotal: lyricFiles.length, imported: 0, failed: [], persistent, startedAt: new Date().toISOString(), playlistId: playlist?.id, playlistAdded: playlist ? 0 : undefined };
  state.importTask = task; state.view = 'library'; render();
  const importedIds = new Set<string>();
  const hasLocalAudio = (track: Track) => track.availability === 'persistent' || objectUrls.has(track.id) || sessionFiles.has(track.id);
  const addToTargetPlaylist = (id: string) => {
    if (playlist && !playlist.trackIds.includes(id)) { playlist.trackIds.push(id); task.playlistAdded = (task.playlistAdded || 0) + 1; }
  };
  let savePersistent = persistent;
  if (savePersistent && audioFiles.length && typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate().catch(() => undefined);
    const capacity = estimatePersistentImport(audioFiles, estimate);
    if (!capacity.allowed) {
      savePersistent = false;
      task.persistent = false;
      task.failed.push({ fileName: '持久化导入', reason: `预计需要 ${readableBytes(capacity.bytes)}，可用空间约 ${readableBytes(capacity.available || 0)}，已回退到会话导入` });
      notify('浏览器可用存储空间不足，已回退到会话导入', 'error');
      render();
    }
  }
  const advance = async () => { task.processed += 1; render(); await yieldToBrowser(); };
  try {
    for (const file of audioFiles) {
      if (controller.signal.aborted) break;
      let id: string | undefined; let savedToDb = false;
      try {
        throwIfAborted(controller.signal);
        id = uid(); const url = objectUrls.create(file, file.type, id); sessionFiles.set(id, file);
        // The tag parser already knows duration for common containers. Only
        // start a separate browser media probe when that value is unavailable.
        let metadata: Awaited<ReturnType<typeof parseBlob>> | undefined;
        try { metadata = await parseBlobWithAbort(file, controller.signal); } catch (error) { if ((error as DOMException).name === 'AbortError') throw error; }
        throwIfAborted(controller.signal);
        const parsedDuration = metadata?.format.duration;
        const durationMs = Number.isFinite(parsedDuration) && parsedDuration! > 0 ? parsedDuration! * 1000 : await probeDuration(url, controller.signal);
        throwIfAborted(controller.signal);
        const relativePath = importRelativePath(file); const leafName = relativePath.split(/[\\/]/).pop() || file.name;
        const base = leafName.replace(/\.[^.]+$/, ''); const [fallbackArtist, fallbackTitle] = base.includes(' - ') ? base.split(' - ', 2) : ['', base];
        const fingerprint = importFingerprint(file, durationMs);
        const candidates = state.tracks.filter((item) => item.sourceType === 'local' && (item.fingerprint === fingerprint || (item.fileName === relativePath && item.size === file.size && Math.abs((item.durationMs || 0) - (durationMs || 0)) < 1000)));
        const signature = await localFileSampleSignature(file); throwIfAborted(controller.signal);
        let duplicate: Track | undefined;
        for (const candidate of candidates) {
          let expected = candidate.metadata?.localFileSampleSignature;
          // Legacy available rows need their actual saved audio checked before
          // deciding whether a same-name/size/duration file is a duplicate.
          if (!expected && hasLocalAudio(candidate)) {
            const saved = sessionFiles.get(candidate.id) || (candidate.availability === 'persistent' ? await loadAudioBlob(candidate.id).catch(() => undefined) : undefined);
            throwIfAborted(controller.signal);
            if (saved) {
              expected = await localFileSampleSignature(saved); throwIfAborted(controller.signal);
              Vue.set(candidate, 'metadata', { ...candidate.metadata, localFileSampleSignature: expected });
            }
          }
          if (expected === signature) { duplicate = candidate; break; }
        }
        if (!duplicate) {
          const unavailable = candidates.filter(candidate => !hasLocalAudio(candidate));
          if (unavailable.some(candidate => candidate.metadata?.localFileSampleSignature))
            throw new Error('原路径文件内容与已保存的采样签名不符，请重新选择原文件夹');
          if (unavailable.length > 1) throw new Error('同名文件无法确认所属原目录，请重新选择原文件夹恢复');
          duplicate = unavailable[0];
          if (!duplicate && candidates.some(candidate => !candidate.metadata?.localFileSampleSignature))
            throw new Error('无法核对已保存音频的内容，请先重新选择原文件恢复');
        }
        if (duplicate && hasLocalAudio(duplicate)) {
          objectUrls.revoke(id); sessionFiles.delete(id);
          importedIds.add(duplicate.id);
          addToTargetPlaylist(duplicate.id);
          task.failed.push({ fileName: relativePath, reason: '检测到同名、同大小且时长相同的重复文件，已跳过' });
          continue;
        }
        const previousPresentation = duplicate ? localPresentations.get(duplicate.id) : undefined;
        const previous = duplicate && previousPresentation && presentationMatches(duplicate, previousPresentation) ? previousPresentation : undefined;
        const duplicateFingerprint = duplicate?.fingerprint, duplicateSize = duplicate?.size;
        if (duplicate) {
          objectUrls.revoke(id); sessionFiles.delete(id);
          id = duplicate.id;
          objectUrls.create(file, file.type, id); sessionFiles.set(id, file);
        }
        if (!(Number.isFinite(durationMs) && durationMs! > 0) && (!metadata || metadata.format.hasAudio === false || !(metadata.format.codec || metadata.format.container || metadata.format.sampleRate))) throw new Error('无法读取有效音频');
        const common = metadata?.common; const picture = common?.picture ? selectCover(common.picture) : undefined;
        const artwork = (picture?.data.byteLength ? await thumbnailArtwork(picture) : undefined) || previous?.artwork;
        throwIfAborted(controller.signal);
        if (duplicate && (trackById(id) !== duplicate || duplicate.fingerprint !== duplicateFingerprint || duplicate.size !== duplicateSize)) throw new Error('原歌曲已更换或移除，请重新导入原文件');
        if (artwork?.size) { const artworkUrl = URL.createObjectURL(artwork); if (artworkUrls.has(id)) URL.revokeObjectURL(artworkUrls.get(id)!); artworkUrls.set(id, artworkUrl); }
        if (savePersistent) {
          try { await storeAudioBlob(id, file, true); savedToDb = true; } catch (error) {
            const limitRejected = error instanceof Error && error.name === 'AudioCacheLimitError';
            if (!limitRejected) { savePersistent = false; task.persistent = false; }
            task.failed.push({ fileName: relativePath, reason: limitRejected ? error.message : isQuotaExceededError(error) ? '浏览器存储配额不足，已回退到会话导入' : '浏览器持久化写入失败，已回退到会话导入' });
          }
        }
        if (duplicate && (trackById(id) !== duplicate || duplicate.fingerprint !== duplicateFingerprint || duplicate.size !== duplicateSize)) throw new Error('原歌曲已更换或移除，请重新导入原文件');
        const actualDurationMs = metadata?.format.duration ? metadata.format.duration * 1000 : durationMs;
        const track: Track = { id, sourceType: 'local', sourceId: savedToDb ? 'browser-indexeddb' : 'browser-session', externalId: relativePath, title: common?.title || fallbackTitle || leafName, artists: common?.artists?.length ? common.artists : (fallbackArtist ? [fallbackArtist] : ['未知艺术家']), album: common?.album || '本地导入', durationMs: actualDurationMs, fingerprint: importFingerprint(file, actualDurationMs), size: file.size, artworkRef: artworkUrls.get(id), availability: savedToDb ? 'persistent' : 'session', fileName: relativePath, metadata: { localFileSampleSignature: signature }, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
        const lyrics = metadata ? readEmbeddedLyrics(metadata, actualDurationMs) : previous?.lyrics || emptyEmbeddedLyrics();
        if (!metadata) lyrics.warnings.push('无法读取此文件的内嵌封面或歌词；未修改原始音频。');
        const latestLyrics = latestLocalPresentationLyrics(duplicate || track, lyrics);
        const cached: LocalPresentation = { id, version: LOCAL_PRESENTATION_VERSION, fingerprint: track.fingerprint || '', size: track.size, artwork, embeddedCover: Boolean(artwork?.size), ...latestLyrics, id3v22LyricsChecked: true, status: metadata ? 'ready' : 'failed', updatedAt: track.updatedAt };
        if (duplicate) Vue.set(state.tracks, state.tracks.indexOf(duplicate), { ...track, ...duplicate, sourceId: track.sourceId, availability: track.availability, artworkRef: track.artworkRef, fingerprint: track.fingerprint, size: track.size, metadata: { ...duplicate.metadata, ...track.metadata }, updatedAt: track.updatedAt }); else state.tracks.push(track);
        const imported = trackById(id)!; applyLocalPresentation(imported, cached); localPresentations.set(id, cached); presentationPending.delete(id); presentationAttempted.delete(id);
        await saveLocalPresentation(imported, cached);
        task.imported += 1;
        importedIds.add(id);
        addToTargetPlaylist(id);
      } catch (error) {
        if ((error as DOMException).name === 'AbortError') { if (id && savedToDb) void deleteAudioBlob(id); if (id && artworkUrls.has(id)) URL.revokeObjectURL(artworkUrls.get(id)!); if (id) { objectUrls.revoke(id); sessionFiles.delete(id); artworkUrls.delete(id); } break; }
        if (id) { if (artworkUrls.has(id)) URL.revokeObjectURL(artworkUrls.get(id)!); objectUrls.revoke(id); sessionFiles.delete(id); artworkUrls.delete(id); }
        task.failed.push({ fileName: file.name, reason: error instanceof Error ? error.message : '无法读取文件' });
      } finally { await advance(); }
    }
    for (const lyricFile of lyricFiles) {
      if (controller.signal.aborted) break;
      try {
        throwIfAborted(controller.signal);
        const lyricPath = importRelativePath(lyricFile).replaceAll('\\', '/');
        const stem = lyricPath.replace(/\.[^.]+$/, '').toLowerCase(); const name = stem.split('/').pop();
        const candidates = state.tracks.filter(track => track.sourceType === 'local' && ((track.fileName?.replaceAll('\\', '/').split('/').pop() || '').replace(/\.[^.]+$/, '').toLowerCase() === name || track.title.toLowerCase() === name));
        const exact = candidates.filter(track => (track.fileName || '').replaceAll('\\', '/').replace(/\.[^.]+$/, '').toLowerCase() === stem);
        // A folder picker supplies an explicit relative directory. Never attach
        // its sidecar to a different folder just because the basename is unique.
        const matches = exact.length || lyricPath.includes('/') ? exact : candidates;
        const imported = exact.filter(track => importedIds.has(track.id));
        const target = imported.length === 1 ? imported[0] : matches.length === 1 ? matches[0] : undefined;
        if (!target) throw new Error(matches.length ? '找到多个同名本地音频，请将歌词与对应音频一起导入' : '没有找到同名本地音频');
        const lyricText = await readLyricText(lyricFile);
        const parsed = parseTimedLyrics(lyricFile.name, lyricText); throwIfAborted(controller.signal);
        if (!parsed.length) throw new Error('没有可识别的时间轴');
        const amllLyrics = parseAmllLyrics(lyricFile.name, lyricText, parsed);
        Vue.set(target, 'lyrics', parsed); Vue.set(target, 'amllLyrics', amllLyrics.length ? amllLyrics : undefined); state.lyrics[target.id] = parsed;
        target.updatedAt = new Date().toISOString();
        await cacheLocalLyricOverride(target);
      } catch (error) {
        if ((error as DOMException).name === 'AbortError') break;
        task.failed.push({ fileName: lyricFile.name, reason: error instanceof Error ? error.message : '无法读取歌词' });
      } finally { await advance(); }
    }
  } finally {
    // Keep the displayed usage accurate even when a batch was cancelled or
    // partially failed, without a full database scan for every imported file.
    if (persistent) await refreshAudioCache(true);
    task.status = controller.signal.aborted ? 'cancelled' : 'completed'; task.finishedAt = new Date().toISOString(); activeImportAbort = null; persist();
    const failureSummary = task.failed.length ? `，${task.failed.length} 项失败` : '';
    const message = task.status === 'cancelled' ? `导入已取消：已保留 ${task.imported} 首，已处理 ${task.processed}/${task.total} 项${failureSummary}` : `导入完成：成功 ${task.imported} 首${lyricFiles.length ? `，处理 ${lyricFiles.length} 个歌词文件` : ''}${failureSummary}`;
    state.view = 'library'; notify(message, task.failed.length ? 'error' : task.status === 'cancelled' ? 'info' : 'success');
  }
}
const restoringLocalIds = new Set<string>();
async function restoreLocalFile(id: string, file: File, persistent = false) {
  const track = trackById(id);
  if (!track || track.sourceType !== 'local' || track.availability !== 'unavailable' || restoringLocalIds.has(id)) return false;
  restoringLocalIds.add(id);
  try {
    let parsed: Awaited<ReturnType<typeof parseBlob>> | undefined;
    try { parsed = await parseBlob(file); } catch { /* A browser duration probe can still validate supported audio. */ }
    let durationMs = parsed?.format.duration ? parsed.format.duration * 1000 : undefined;
    if (durationMs === undefined) {
      const url = URL.createObjectURL(file);
      try { durationMs = await probeDuration(url, new AbortController().signal); }
      finally { URL.revokeObjectURL(url); }
    }
    const signature = await localFileSampleSignature(file);
    const error = localFileRecoveryError(track, file, durationMs, signature, state.tracks);
    if (error || parsed?.format.hasAudio === false) { notify(error || '所选文件不是有效音频，请重新选择原文件', 'error'); return false; }
    // The picker restores only its explicit target, never another same-named
    // row. A removal/reimport while parsing must invalidate this pending action.
    if (trackById(id) !== track || track.availability !== 'unavailable') return false;
    const previousPresentation = localPresentations.get(id);
    const previous = previousPresentation && presentationMatches(track, previousPresentation) ? previousPresentation : undefined;
    const fingerprint = track.fingerprint, size = track.size;
    const picture = parsed?.common.picture ? selectCover(parsed.common.picture) : undefined;
    const artwork = (picture?.data.byteLength ? await thumbnailArtwork(picture) : undefined) || previous?.artwork;
    let savedToDb = false;
    if (persistent) {
      try { await storeAudioBlob(id, file); savedToDb = true; }
      catch { /* Keep a valid selection usable for this session when audio storage is full/denied. */ }
    }
    if (trackById(id) !== track || track.availability !== 'unavailable' || track.fingerprint !== fingerprint || track.size !== size) return false;
    sessionFiles.set(id, file); objectUrls.create(file, file.type, id);
    track.sourceId = savedToDb ? 'browser-indexeddb' : 'browser-session';
    track.availability = savedToDb ? 'persistent' : 'session';
    track.updatedAt = new Date().toISOString();
    Vue.set(track, 'metadata', { ...track.metadata, localFileSampleSignature: signature });
    const lyrics = parsed ? readEmbeddedLyrics(parsed, durationMs) : previous?.lyrics || emptyEmbeddedLyrics();
    if (!parsed) lyrics.warnings.push('无法读取此文件的内嵌封面或歌词；未修改原始音频。');
    const latestLyrics = latestLocalPresentationLyrics(track, lyrics);
    const cached: LocalPresentation = { id, version: LOCAL_PRESENTATION_VERSION, fingerprint: track.fingerprint || '', size: track.size, artwork, embeddedCover: Boolean(artwork?.size), ...latestLyrics, id3v22LyricsChecked: true, status: parsed ? 'ready' : 'failed', updatedAt: track.updatedAt };
    if (artwork?.size && artworkUrls.has(id)) { URL.revokeObjectURL(artworkUrls.get(id)!); artworkUrls.delete(id); }
    localPresentations.set(id, cached); applyLocalPresentation(track, cached); presentationAttempted.delete(id);
    await saveLocalPresentation(track, cached);
    persist(); render();
    notify(persistent && !savedToDb ? '已恢复原歌曲；浏览器音频缓存不可用，本次会话可播放' : '已恢复原歌曲，歌单与喜欢关系已保留', 'success');
    return true;
  } catch (error) { notify(error instanceof Error ? error.message : '无法读取所选文件，请重新选择原文件', 'error'); return false; }
  finally { restoringLocalIds.delete(id); }
}
const cancelPlaybackRequest = () => {
  removeResumeSeek?.();
  removeResumeSeek = null;
  playbackController.cancelPendingPlay();
};
function pausePlaybackForNavigation() {
  cancelQualitySwitch();
  cancelPlaybackRequest();
  playbackIntent = null;
  audio.pause();
  state.isPlaying = false;
  render();
}
function reservePlaybackRequest() {
  cancelQualitySwitch();
  cancelPlaybackRequest();
  playbackIntent = null;
  return playbackController.snapshot().requestId;
}
function isPlaybackRequestCurrent(ticket: number) { return playbackController.isCurrentRequest(ticket); }
async function playTrack(id: string, listOverride?: readonly string[], options: { autoplay?: boolean; startPosition?: number; playbackRate?: number; preserveCurrentState?: boolean; preparedPlayback?: PreparedOnlinePlayback; onlineRequest?: OnlineRequest; allowRelayFallback?: boolean } = {}) {
  if (!options.preserveCurrentState) cancelQualitySwitch();
  if (!retainSearchTrack(id)) return;
  if (state.playbackFailure?.trackId !== id) clearPlaybackFailure();
  ui.playerPrepared = true;
  prefetchTrackPresentation(id);
  if (listOverride !== undefined) preferredListForPlay = [...listOverride];
  else if (preferredListForPlay && !preferredListForPlay.includes(id) && !playbackController.getPlayNext().includes(id)) preferredListForPlay = null;
  syncPlaybackController();
  const intent: PlaybackIntent = { id, autoplay: options.autoplay !== false, startPosition: Number.isFinite(options.startPosition) ? Math.max(0, options.startPosition!) : 0, playbackRate: Number.isFinite(options.playbackRate) ? Math.max(0.25, Math.min(4, options.playbackRate!)) : undefined, preserveCurrentState: options.preserveCurrentState === true, preparedPlayback: options.preparedPlayback, onlineRequest: options.onlineRequest ? { ...options.onlineRequest } : undefined };
  playbackIntent = intent;
  if (options.preserveCurrentState && qualitySwitchTask?.trackId === id) qualitySwitchTask.intent = intent;
  const result = await playbackController.play(id);
  if (playbackIntent === intent) playbackIntent = null;
  if (result.status === 'stale') return result;
  if (options.allowRelayFallback !== false && result.status === 'error' && shouldRelayNativePlayback(onlinePlayback.resolved, id, result.error, audio.error?.code)) {
    const resolved = onlinePlayback.resolved!;
    return playTrack(id, listOverride, { ...options, startPosition: intent.startPosition, onlineRequest: { sourceId: resolved.sourceId, quality: resolved.requestedQuality, transport: 'relay' } });
  }
  ui.playNextIds = playbackController.getPlayNext();
  if (result.status === 'played') {
    if (onlinePlayback.resolved?.trackId === id && !audio.paused) onlinePlayback.resolved.playbackVerified = true;
    clearPlaybackFailure();
    state.isPlaying = !audio.paused;
    persist();
    render();
    if (!audio.paused && !document.hidden) playbackController.prefetchNext();
  }
  return result;
}
async function retryPlayback() {
  const failure = state.playbackFailure;
  if (!failure) return null;
  const position = state.currentId === failure.trackId ? Math.max(0, state.currentTime) : 0;
  const onlineRequest = retryRelayRequest(failure.trackId);
  return playTrack(failure.trackId, undefined, { startPosition: position, ...(onlineRequest ? { onlineRequest } : {}) });
}
function togglePlay() {
  if (qualitySwitchTask?.intent && qualitySwitchTask.phase !== 'prepare') {
    qualitySwitchTask.intent.autoplay = !qualitySwitchTask.intent.autoplay;
    if (!qualitySwitchTask.intent.autoplay) audio.pause();
    else if (qualitySwitchTask.nativePromotion) void audio.play().catch(() => {});
    state.isPlaying = qualitySwitchTask.nativePromotion === true && !audio.paused; persist(); render(); return;
  }
  if (playbackIntent?.id === state.currentId && playbackIntent.onlineRequest) {
    cancelPlaybackRequest();
    playbackIntent = null;
    audio.pause();
    state.isPlaying = false; persist(); render();
    return;
  }
  // A direct play/pause action supersedes any directory still being collected,
  // including when no track has reached Audio yet.
  cancelPlaybackRequest();
  playbackIntent = null;
  if (!state.currentId) { const ids = activePlaybackList(); const first = ids.map(trackById).find(Boolean); if (first) void playTrack(first.id, ids); else notify('播放列表为空，请从音乐库选择歌曲', 'info'); return; }
  if (audio.paused) {
    // The selected song may have failed before replacing the previous source.
    // Resume must resolve that selection, never relabel the old loaded audio.
    if (protectedAudioId !== state.currentId) return void playTrack(state.currentId, activePlaybackList());
    if (!audio.getAttribute('src')) return void playTrack(state.currentId, activePlaybackList(), { startPosition: state.positions[state.currentId] || 0 });
    const track = currentTrack();
    const resolved = onlinePlayback.resolved;
    const expiresAt = resolved?.expiresAt ? Date.parse(resolved.expiresAt) : NaN;
    // Browsers can reject playback without retaining audio.error. Renew the
    // failed online track with its original source selection on explicit retry.
    const failedOnlineTrack = track?.sourceType === 'online' && state.playbackFailure?.trackId === track.id;
    if (track?.sourceType === 'online' && resolved?.trackId === track.id && (audio.error || failedOnlineTrack || (resolved.transport === 'relay' && Number.isFinite(expiresAt) && expiresAt <= Date.now()))) {
      const position = Number.isFinite(audio.currentTime) ? Math.max(0, audio.currentTime) : state.positions[track.id] || 0;
      const onlineRequest = retryRelayRequest(track.id) || { sourceId: resolved.sourceId, quality: resolved.requestedQuality || resolved.quality, transport: resolved.transport };
      return void playTrack(track.id, activePlaybackList(), { startPosition: position, onlineRequest });
    }
    const requestId = playbackController.snapshot().requestId;
    const resumedId = state.currentId;
    const failure = state.playbackFailure;
    void audio.play().then(() => {
      if (!playbackController.isCurrentRequest(requestId) || state.currentId !== resumedId) return;
      if (state.playbackFailure !== failure && state.playbackFailure?.trackId === resumedId) return;
      state.isPlaying = true;
      if (failure?.trackId === resumedId && state.playbackFailure === failure) clearPlaybackFailure();
      persist();
      render();
    }).catch(() => {
      if (!playbackController.isCurrentRequest(requestId)) return;
      const id = state.currentId;
      if (id) rememberPlaybackFailure(id, 'play', '播放失败，请确认文件权限仍有效');
      notify('播放失败，请确认文件权限仍有效', 'error');
    });
  } else {
    audio.pause(); state.isPlaying = false; persist(); render();
  }
}
function pauseCurrentPlayback() {
  if (qualitySwitchTask?.intent) qualitySwitchTask.intent.autoplay = false;
  else { cancelPlaybackRequest(); playbackIntent = null; }
  audio.pause(); state.isPlaying = false; persist(); render();
}
function toggleMute() {
  audio.muted = !audio.muted;
  ui.muted = audio.muted;
}
const modeLabel = (mode: PlaybackMode) => ({ sequence: '顺序播放', 'repeat-all': '列表循环', 'repeat-one': '单曲循环', shuffle: '随机播放' }[mode]);
function cyclePlayMode() { const modes: PlaybackMode[] = ['sequence', 'repeat-all', 'repeat-one', 'shuffle']; state.playMode = modes[(modes.indexOf(state.playMode) + 1) % modes.length]; persist(); notify(`已切换为${modeLabel(state.playMode)}`, 'success'); render(); }
function nextTrack(direction = 1, cause: 'manual' | 'ended' = 'manual') {
  syncPlaybackController();
  const next = playbackController.next(direction > 0 ? 1 : -1, cause);
  ui.playNextIds = [...playbackController.snapshot().playNext];
  if (!next) {
    if (direction > 0) {
      cancelQualitySwitch();
      cancelPlaybackRequest();
      audio.pause();
      state.isPlaying = false;
      persist();
      notify('播放列表已结束', 'info');
    }
    return;
  }
  void playTrack(next, activePlaybackList());
}
let queueRemovalGeneration = 0;
let removingCurrentId: string | null = null;
async function removeCurrentFromQueue() {
  const id = state.currentId;
  if (!id || removingCurrentId === id) return;
  const generation = ++queueRemovalGeneration;
  removingCurrentId = id;
  syncPlaybackController();
  const snapshot = playbackController.snapshot();
  const list = snapshot.list.filter(trackId => trackId !== id);
  const autoplay = !audio.paused;
  // Select with the existing queue model before changing the list, preserving
  // shuffle / play-next ordering. A single repeating entry cannot select itself.
  const next = playbackController.next(1, 'manual');
  const adjacent = list[Math.max(0, Math.min(snapshot.list.indexOf(id), list.length - 1))] || null;
  const target = next && next !== id ? next : adjacent;
  cancelPlaybackRequest();
  audio.pause();
  state.queue = [...list];
  preferredListForPlay = [...list];
  playbackController.setPlayNext(playbackController.getPlayNext().filter(trackId => trackId !== id));
  ui.playNextIds = playbackController.getPlayNext();
  syncPlaybackController();
  persist();
  render();
  notify('已从播放列表移除当前歌曲，音乐库和歌单仍保留', 'success');
  try {
    if (target) {
      const result = await playTrack(target, list, { autoplay });
      if (!result || result.status === 'stale' || !playbackController.isCurrentRequest(result.requestId)) return;
      if (result.status !== 'played') {
        playbackController.setCurrent(null);
        audio.removeAttribute('src');
        audio.load();
      }
    } else {
      playbackController.setCurrent(null);
      state.currentId = null;
      state.currentTime = 0;
      state.duration = 0;
      state.isPlaying = false;
      audio.removeAttribute('src');
      audio.load();
      if (protectedAudioId) objectUrls.unprotect(protectedAudioId);
      protectedAudioId = null;
    }
    persist();
    render();
  } finally {
    if (generation === queueRemovalGeneration) removingCurrentId = null;
  }
}
function toggleFavorite(id: string) { const track = retainSearchTrack(id); if (!track) return; if (!state.favorites.includes(id)) Vue.set(track, 'metadata', { ...track.metadata, libraryOwned: true }); state.favorites = state.favorites.includes(id) ? state.favorites.filter((x) => x !== id) : [...state.favorites, id]; persist(); render(); }
function addToQueue(id: string) {
  if (!retainSearchTrack(id)) return;
  if (!state.queue.includes(id)) state.queue.push(id);
  if (preferredListForPlay && !preferredListForPlay.includes(id)) preferredListForPlay = [...preferredListForPlay, id];
  syncPlaybackController();
  persist(); notify('已加入播放队列', 'success'); render();
}
function addTracksToQueue(tracks: Track[]) {
  if (!tracks.length) return;
  registerTracks(tracks);
  const additions = tracks.map(track => track.id).filter(id => !state.queue.includes(id));
  const contextAdditions = preferredListForPlay ? tracks.map(track => track.id).filter(id => !preferredListForPlay!.includes(id)) : [];
  if (!additions.length && !contextAdditions.length) return notify('所选歌曲已在播放队列中', 'info');
  state.queue.push(...additions);
  if (preferredListForPlay) preferredListForPlay = [...preferredListForPlay, ...contextAdditions];
  syncPlaybackController();
  persist(); notify(`已将 ${new Set([...additions, ...contextAdditions]).size} 首歌曲加入播放队列`, 'success'); render();
}
function addToPlayNext(id: string) {
  if (!retainSearchTrack(id)) return;
  syncPlaybackController();
  if (!playbackController.enqueueNext(id)) return notify('这首歌已在“下一首播放”中或正在播放', 'info');
  notify('已安排为下一首播放', 'success');
  render();
}
function moveQueue(id: string, direction: -1 | 1) { const index = state.queue.indexOf(id); const target = index + direction; if (index < 0 || target < 0 || target >= state.queue.length) return; [state.queue[index], state.queue[target]] = [state.queue[target], state.queue[index]]; persist(); render(); }
function addToPlaylist(id: string, playlistId?: string) {
  if (!playlistId) throw new Error('请选择目标歌单');
  const playlist = state.playlists.find(item => item.id === playlistId);
  if (!playlist) throw new Error('没有找到这个歌单，请重新选择');
  if (!trackById(id) && !state.remoteSearchResults.some(track => track.id === id)) throw new Error('没有找到这首歌曲');
  if (playlist.trackIds.includes(id)) { notify(`「${playlist.name}」已包含这首歌曲`, 'info'); return 0; }
  return addTracksToPlaylist([id], playlist.id);
}
function playPlaylist(id: string) {
  const playlist = state.playlists.find((item) => item.id === id);
  const ids = playlist?.trackIds.filter((trackId) => trackById(trackId)) || [];
  if (!ids.length) return notify('歌单里还没有可播放的歌曲', 'info');
  state.queue = [...ids];
  persist();
  void playTrack(ids[0], ids);
}
function queuePlaylist(id: string) {
  const playlist = state.playlists.find((item) => item.id === id);
  const ids = playlist?.trackIds.filter((trackId) => trackById(trackId)) || [];
  if (!ids.length) return notify('歌单里还没有可加入队列的歌曲', 'info');
  const next = ids.filter((trackId) => !state.queue.includes(trackId));
  state.queue.push(...next);
  persist();
  notify(`已将 ${next.length} 首歌曲加入队列`, 'success');
  render();
}

// Vue updates reactive state without replacing any input node.
function render() { syncMediaSession(); }

const access = Vue.observable({ ready: false, configured: false, authenticated: true });
const PLAYBACK_RATE_KEY = 'ypm.playback-rate.v1';
const savedPlaybackRate = (() => {
  try { const value = Number(localStorage.getItem(PLAYBACK_RATE_KEY)); return Number.isFinite(value) && value >= 0.5 && value <= 2 ? value : 1; } catch { return 1; }
})();
const ui = Vue.observable({ queueOpen: false, audioSettingsOpen: false, lyricsOpen: false, playerPrepared: false, positionRevision: 0, editTrackId: '', muted: audio.muted, playbackRate: savedPlaybackRate, sourcePreferences: readSourcePreferences(), playNextIds: [] as string[] });
audio.playbackRate = savedPlaybackRate;
audio.defaultPlaybackRate = savedPlaybackRate;
const ONLINE_PREFERENCES_KEY = 'ypm.online-playback.v1';
const catalogSettings = Vue.observable({ platform: localStorage.getItem('ypm.catalog-platform.v1') === 'tx' ? 'tx' : 'wy' });
function setCatalogPlatform(platform: string) { if (!['wy', 'tx'].includes(platform)) return; catalogSettings.platform = platform; try { localStorage.setItem('ypm.catalog-platform.v1', platform); } catch { /* The current session remains usable. */ } }
const qualityNames: Record<string, string> = { standard: '标准', exhigh: '极高', lossless: '无损', hires: 'Hi-Res', jyeffect: '高清臻音', jymaster: '母带', flac24bit: '源24bit档', atmos: '臻品音质', atmos_plus: '臻品全景声' };
const validQuality = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9_-]{1,40}$/.test(value);
function readOnlinePreferences() {
  try {
    const value = JSON.parse(localStorage.getItem(ONLINE_PREFERENCES_KEY) || '{}');
    const selections = Object.fromEntries(Object.entries(value.selections || {}).filter(([id, item]) => {
      const selection = item as OnlineSelection;
      const cloud = /^cloud:netease:[1-9]\d{0,31}:[1-9]\d{0,15}$/.test(id);
      return (id.startsWith('online:') || cloud && selection?.sourceId === 'netease-cloud') && selection && /^[a-zA-Z0-9_-]+$/.test(selection.sourceId) && validQuality(selection.quality);
    })) as Record<string, OnlineSelection>;
    return { defaultQuality: validQuality(value.defaultQuality) ? value.defaultQuality : 'standard', defaultQualityByPlatform: migratePlatformDefaultQualities(value.defaultQualityByPlatform, value.defaultQuality), selections };
  } catch { return { defaultQuality: 'standard', defaultQualityByPlatform: migratePlatformDefaultQualities(undefined), selections: {} as Record<string, OnlineSelection> }; }
}
const ONLINE_TRANSPORT_KEY = 'ypm.online-transport.v1';
function readOnlineTransport(): OnlineTransport {
  try { const value = localStorage.getItem(ONLINE_TRANSPORT_KEY); if (value === 'browser' || value === 'relay') return value; } catch { /* Use deployment default. */ }
  return cloudflareRuntime ? 'browser' : 'relay';
}
const onlinePlayback = Vue.observable({ ...readOnlinePreferences(), transport: readOnlineTransport(), resolved: null as OnlineResolution | null, busyTrackId: '', error: '', errorTrackId: '' });
function setOnlineTransport(value: OnlineTransport) {
  if (value !== 'browser' && value !== 'relay') return;
  onlinePlayback.transport = value;
  playbackController.clearPrefetch();
  try { localStorage.setItem(ONLINE_TRANSPORT_KEY, value); } catch { notify('浏览器无法保存取流偏好', 'error'); }
}
async function resolveOnlineRequest(trackId: string, quality: string, signal?: AbortSignal, transport: OnlineTransport = onlinePlayback.transport, purpose: 'playback' | 'download' = 'playback'): Promise<Record<string, any> & { transport: OnlineTransport }> {
  try {
    return await resolveOnlineWithFallback({
      trackId, quality, signal, transport,
      browser: async (id, requestedQuality, requestSignal) => {
        const data = await (purpose === 'download' ? resolveBrowserDownload : resolveBrowserPlayback)(id, requestedQuality, requestSignal);
        return { ...data, streamUrl: data.mediaUrl };
      },
      relay: async (id, requestedQuality, requestSignal) => {
        const response = await apiFetch('/api/playback/resolve', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ trackId: id, quality: requestedQuality }), signal: requestSignal });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const code = payload?.error?.code || payload?.code;
          const message = typeof payload?.error === 'string' ? payload.error : payload?.error?.message;
          throw Object.assign(new Error(message || code || `请求失败 (${response.status})`), { code, diagnostics: payload?.error?.diagnostics || payload?.diagnostics });
        }
        if (!payload?.data) throw new Error('接口未返回音频解析结果');
        return payload.data;
      },
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    const code = (error as { code?: string })?.code || '';
    const messages: Record<string, string> = {
      SOURCE_QUALITY_UNSUPPORTED: '所选音源不支持该平台或没有可用的较低音质，请切换音源。',
      SOURCE_QUALITY_UNAVAILABLE: '已尝试所选音质及可用的较低音质，当前仍无法取得该歌曲。',
      SOURCE_QUALITY_MISMATCH: '音源返回的音频格式与档位不符，较低档位也未能通过验证。',
      SOURCE_TIMEOUT: '本机连接音源超时，请稍后重试或在设置切换网站代理。',
      SOURCE_BROWSER_NETWORK: '浏览器无法直连音源，可能受本机网络或跨域限制；可在设置切换网站代理。',
      SOURCE_CORS_UNAVAILABLE: '上游未允许浏览器读取响应，请在设置切换网站代理。',
    };
    if (messages[code]) throw Object.assign(new Error(messages[code]), { code, diagnostics: (error as { diagnostics?: unknown })?.diagnostics });
    if (transport === 'browser' && (error instanceof TypeError || (error as Error)?.name === 'TimeoutError')) throw Object.assign(new Error('浏览器直连失败，可能受本机网络或上游跨域限制；可在设置切换网站代理。'), { code: 'SOURCE_BROWSER_NETWORK' });
    throw error;
  }
}
const persistOnlinePreferences = () => {
  try { localStorage.setItem(ONLINE_PREFERENCES_KEY, JSON.stringify({ defaultQuality: onlinePlayback.defaultQuality, defaultQualityByPlatform: onlinePlayback.defaultQualityByPlatform, selections: onlinePlayback.selections })); }
  catch { notify('浏览器无法保存音源与音质偏好，请检查存储空间', 'error'); }
};
const qualityLabel = (quality: string, platform?: Track | string) => platformQualityLabel(quality, platform || currentTrack());
function onlineSelection(track?: Track): OnlineSelection {
  const selection = track && onlinePlayback.selections[track.id] || { sourceId: track?.sourceId || '', quality: defaultOnlineQuality(track) };
  const cloud = privateCloudSource(track);
  if (!cloud) return selection;
  const qualities = cloud.qualitiesByPlatform!.wy;
  return { sourceId: cloud.id, quality: qualities.includes(selection.quality) ? selection.quality : selection.quality === 'jyeffect' ? 'hires' : 'standard' };
}
function defaultOnlineQuality(platform?: Track | string) { return onlinePlayback.defaultQualityByPlatform[qualityPlatform(platform || currentTrack() || catalogSettings.platform)]; }
function privateCloudSource(track?: Track): Source | null {
  if (track?.sourceType !== 'online' || track.sourceId !== 'netease-cloud' || track.metadata?.neteaseCloud?.accountOnly !== true) return null;
  // This is the account API's request contract, not a promise that an uploaded
  // recording contains every tier. Its verified response reports any fallback.
  return { id: 'netease-cloud', name: '网易云盘（所属账号）', kind: 'online', status: '需要所属账号授权', capabilities: ['resolvePlayback'], platforms: ['wy'], qualitiesByPlatform: { wy: ['hires', 'lossless', 'exhigh', 'standard'] } };
}
function onlineSourcesFor(track?: Track) {
  if (!track || track.sourceType !== 'online') return [];
  const cloud = privateCloudSource(track);
  if (cloud) return [cloud];
  return state.sources.filter(source => source.enabled !== false && source.platforms?.includes(onlinePlatform(track)) && source.capabilities?.some(value => value === 'playback' || value === 'resolvePlayback'));
}
function onlineQualitiesFor(sourceId: string, track?: Track): string[] {
  if (!track) return [];
  const qualities = onlineSourcesFor(track).find(source => source.id === sourceId)?.qualitiesByPlatform?.[onlinePlatform(track)];
  return Array.isArray(qualities) ? [...new Set(qualities.filter(validQuality))] : [];
}
function defaultQualityOptions(platform?: Track | string): string[] {
  const cloud = privateCloudSource(typeof platform === 'object' ? platform : !platform ? currentTrack() : undefined);
  if (cloud) return [...cloud.qualitiesByPlatform!.wy];
  return platformQualityOptions(platform || currentTrack() || catalogSettings.platform).map(item => item.value);
}
function setDefaultQuality(quality: string, platform?: Track | string) {
  const key = qualityPlatform(platform || currentTrack() || catalogSettings.platform);
  if (!platformQualityOptions(key).some(item => item.value === quality)) return notify('请选择有效音质', 'error');
  onlinePlayback.defaultQualityByPlatform[key] = quality;
  onlinePlayback.defaultQuality = quality;
  playbackController.clearPrefetch();
  persistOnlinePreferences();
}
function cancelQualitySwitch() {
  const task = qualitySwitchTask;
  if (!task) return;
  qualitySwitchTask = null;
  task.controller.abort();
  if (task.candidate) { detachEqualizer(task.candidate); releaseNativeMedia(task.candidate); task.candidate = null; }
  if (task.phase !== 'prepare') { cancelPlaybackRequest(); if (playbackIntent === task.intent) playbackIntent = null; }
  if (onlinePlayback.busyTrackId === task.trackId) onlinePlayback.busyTrackId = '';
}
async function prepareQualitySwitch(track: Track, sourceId: string, quality: string, transport: OnlineTransport, task: QualitySwitchTask): Promise<PreparedQualityPlayback> {
  const signal = task.controller.signal;
  const exactTrackId = sourceId === track.sourceId ? track.id : `online:${sourceId}:${onlinePlatform(track)}:${track.externalId}`;
  const resolutionRequest = privateCloudSource(track) ? resolvePrivateCloudPlayback(track, quality, signal) : resolveOnlineRequest(exactTrackId, quality, signal, transport);
  const data = await awaitMediaOperation(resolutionRequest, signal, 30000);
  if (signal.aborted) throw new DOMException('音质切换已取消', 'AbortError');
  const { url, resolution } = onlineResolutionFromData(track.id, { sourceId, quality }, exactTrackId, data);
  const candidate = new Audio(); candidate.preload = 'auto'; candidate.muted = true; candidate.volume = 0;
  task.candidate = candidate;
  const relayEq = equalizer.enabled && transport === 'relay';
  try {
    if (relayEq && !attachLocalEqualizer(candidate, url, true)) {
      // A graph is optional: native relay playback remains available if the
      // browser cannot expose this same-origin media to Web Audio.
      markEqualizerBypass();
    }
    candidate.src = url; candidate.load();
    // Latch the primary clock once the detached media reaches canplay. The
    // primary Audio may keep advancing while this seek completes; chasing it
    // forever would make a playing source impossible to hand off.
    await waitForMediaPosition(candidate, () => task.baseline.url ? currentPlaybackTime() : task.baseline.position, signal, 15000, false, () => ui.positionRevision);
    return { trackId: track.id, exactTrackId, sourceId, requestedQuality: quality, url, resolution, media: candidate, expiresAt: Math.min(Date.now() + 30_000, Date.parse(resolution.expiresAt || '') - 5000 || Infinity) };
  } catch (error) {
    if (task.candidate === candidate) { detachEqualizer(candidate); releaseNativeMedia(candidate); task.candidate = null; }
    throw error;
  }
}
async function switchOnlinePlayback(sourceId: string, quality: string) {
  const track = currentTrack();
  if (!track || track.sourceType !== 'online') return false;
  if (!onlineQualitiesFor(sourceId, track).length || !Object.hasOwn(qualityNames, quality)) {
    onlinePlayback.errorTrackId = track.id;
    onlinePlayback.error = '所选音源尚未声明此平台的播放能力，请重新载入音源或选择其他音源。';
    notify(onlinePlayback.error, 'error');
    return false;
  }
  if (privateCloudSource(track) && !onlineQualitiesFor(sourceId, track).includes(quality)) {
    onlinePlayback.errorTrackId = track.id; onlinePlayback.error = '私人云盘只支持所属账号提供的Hi-Res、无损、极高和标准请求档位。';
    notify(onlinePlayback.error, 'error'); return false;
  }
  // Cancellation restores any unconfirmed promotion synchronously. Read the
  // baseline afterwards so a newer choice owns the last successful decoder.
  cancelQualitySwitch();
  const hasProtectedCurrentMedia = protectedAudioId === track.id;
  const previousSignal = activeMediaSignal;
  const baseline = {
    resolution: onlinePlayback.resolved?.trackId === track.id ? onlinePlayback.resolved : null,
    url: hasProtectedCurrentMedia ? audio.getAttribute('src') : null,
    position: hasProtectedCurrentMedia && Number.isFinite(audio.currentTime) ? Math.max(0, audio.currentTime) : Math.max(0, state.currentTime || state.positions[track.id] || 0), autoplay: hasProtectedCurrentMedia && !audio.paused, playbackRate: audio.playbackRate,
  };
  const task: QualitySwitchTask = { trackId: track.id, controller: new AbortController(), candidate: null, phase: 'prepare', intent: null, baseline };
  qualitySwitchTask = task;
  const current = () => qualitySwitchTask === task && state.currentId === track.id;
  const timer = setTimeout(() => task.controller.abort(new Error('音质切换准备超过30秒')), 30000);
  onlinePlayback.busyTrackId = track.id; onlinePlayback.error = ''; onlinePlayback.errorTrackId = '';
  let prepared!: PreparedQualityPlayback;
  const previousAudio = audio;
  try {
    prepared = await prepareQualitySwitch(track, sourceId, quality, onlinePlayback.transport, task);
    if (!current() || task.controller.signal.aborted) return false;
    if (prepared.expiresAt <= Date.now()) throw new Error('已准备的音质地址已失效，请重新切换');
    let latest: NativeOutputIntent;
    const readIntent = (): NativeOutputIntent => {
      const intent = task.intent;
      return latest = {
        position: protectedAudioId === track.id ? currentPlaybackTime() : baseline.position,
        autoplay: intent ? intent.autoplay : baseline.url ? !audio.paused : baseline.autoplay,
        playbackRate: intent?.playbackRate || audio.playbackRate,
        volume: state.volume, muted: ui.muted, revision: ui.positionRevision,
      };
    };
    readIntent(); playbackController.clearPrefetch(); cancelPlaybackRequest(); playbackIntent = null;
    task.candidate = null; // Ownership now belongs to NativeAudioOutput.
    await nativeOutput.promote(prepared.media, {
      signal: task.controller.signal,
      current: () => current() && prepared.expiresAt > Date.now(),
      intent: readIntent,
      activated: () => {
        task.phase = 'handoff'; task.nativePromotion = true;
        task.intent = { id: track.id, autoplay: latest.autoplay, startPosition: latest.position, playbackRate: latest.playbackRate, preserveCurrentState: true };
        protectedAudioId = track.id; objectUrls.protect(track.id); activeMediaSignal = task.controller.signal;
        setEqualizerOwner(audio);
      },
      restored: () => {
        protectedAudioId = hasProtectedCurrentMedia ? track.id : null;
        activeMediaSignal = previousSignal; onlinePlayback.resolved = baseline.resolution;
        task.intent = null;
        state.currentTime = audio.currentTime; state.isPlaying = !audio.paused;
      },
    });
    if (!current()) return false;
    if (task.controller.signal.aborted) throw task.controller.signal.reason;
    onlinePlayback.resolved = prepared.resolution;
    detachEqualizer(previousAudio);
    setEqualizerOwner(audio);
    // Promotion bypasses the ordinary controller's play port, so keep the
    // same measured-metadata path for the selected native stream.
    const signal = task.controller.signal;
    void probePlaybackMetadata({
      resolution: prepared.resolution, trackId: track.id, exactTrackId: prepared.exactTrackId, mediaUrl: prepared.url, signal,
      fetcher: apiFetch,
      current: () => ({ resolution: onlinePlayback.resolved, trackId: state.currentId, mediaUrl: activeMediaSignal === signal ? audio.getAttribute('src') : null }),
      apply: metadata => { for (const [key, value] of Object.entries(metadata)) Vue.set(prepared.resolution, key, value); },
    });
    if (!audio.paused) prepared.resolution.playbackVerified = true;
    state.currentTime = audio.currentTime; state.duration = Number.isFinite(audio.duration) ? audio.duration : state.duration;
    state.positions[track.id] = audio.currentTime; state.isPlaying = !audio.paused;
    clearPlaybackFailure(); persist(); render();
    Vue.set(onlinePlayback.selections, track.id, { sourceId, quality }); persistOnlinePreferences();
    return true;
  } catch (error) {
    if (prepared?.media && prepared.media !== audio) detachEqualizer(prepared.media);
    if (!current()) return false;
    const detail = sourceErrorText(error);
    onlinePlayback.errorTrackId = track.id;
    onlinePlayback.error = `目标音质切换失败：${detail}${baseline.url ? ' 当前保留原音频。' : ''}`;
    notify(onlinePlayback.error, 'error'); return false;
  } finally {
    clearTimeout(timer);
    if (prepared?.media) {
      // A promotion may have committed immediately before a cancellation or
      // stale-request check. Whichever element is no longer the owner must
      // lose its graph before media is released.
      if (audio === prepared.media) detachEqualizer(previousAudio);
      else detachEqualizer(prepared.media);
    }
    if (task.candidate) { releaseNativeMedia(task.candidate); task.candidate = null; }
    if (qualitySwitchTask === task) { qualitySwitchTask = null; onlinePlayback.busyTrackId = ''; }
  }
}
async function refreshSessionAccess() {
  try {
    const response = await window.fetch(apiRequestUrl('/api/session'), { headers: { accept: 'application/json' } });
    if (!response.ok) return;
    const payload = await response.json().catch(() => null);
    access.configured = payload?.data?.configured === true;
    access.authenticated = !access.configured || payload?.data?.authenticated === true;
  } catch { /* A failed session probe must not turn an upstream playback denial into a logout. */ }
}
function readSourcePreferences(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem('ypm.sources.v1') || '{}'); } catch { return {}; }
}
async function api(path: string, init?: RequestInit) {
  const response = await apiFetch(path, init);
  const result = await response.json().catch(() => null);
  if (!response.ok) throw Object.assign(new Error(result?.error?.message || result?.error?.code || `请求失败 (${response.status})`), { code: result?.error?.code || result?.code, diagnostic: result?.error?.diagnostic, ...(path === '/api/accounts/qq/check' && typeof result?.error?.qrToken === 'string' && result.error.qrToken.length <= 48000 ? { qrToken: result.error.qrToken } : {}) });
  return result?.data ?? result;
}
function normalizeTrack(raw: Track): Track {
  return { ...raw, albumId: raw.albumId || raw.metadata?.albumId, artistIds: raw.artistIds || raw.metadata?.artistIds,
    artists: raw.artists?.length ? raw.artists : ['未知艺术家'], album: raw.album || '未知专辑',
    availability: raw.availability || 'remote', createdAt: raw.createdAt || new Date().toISOString(), updatedAt: raw.updatedAt || new Date().toISOString() };
}
function registerTracks(tracks: Track[], options: { library?: boolean } = {}) {
  for (const raw of tracks) {
    const track = normalizeTrack(raw);
    const existing = trackById(track.id);
    if (!existing) state.tracks.push({ ...track, metadata: { ...track.metadata, libraryOwned: track.sourceType !== 'online' || options.library === true } });
    else if (options.library === true) Vue.set(existing, 'metadata', { ...existing.metadata, libraryOwned: true });
  }
  persist();
}
function safeDavConnection(raw: any): DavConnection {
  return {
    id: String(raw?.id || ''),
    name: String(raw?.name || ''),
    url: String(raw?.url || ''),
    rootPath: String(raw?.rootPath || '/'),
    credentialConfigured: raw?.credentialConfigured === true,
    createdAt: typeof raw?.createdAt === 'string' ? raw.createdAt : undefined,
    updatedAt: typeof raw?.updatedAt === 'string' ? raw.updatedAt : undefined,
  };
}
function replaceDavConnection(connection: DavConnection) {
  const existing = state.davConnections.findIndex(item => item.id === connection.id);
  if (existing >= 0) Vue.set(state.davConnections, existing, connection);
  else state.davConnections.push(connection);
}
const davScanTickets = new Map<string, symbol>();
const staleDavScan = () => Object.assign(new Error('连接或扫描任务已变化，请重新扫描。'), { code: 'DAV_SCAN_STALE' });
function syncDavTracks(connectionId: string, rawTracks: Track[]) {
  const scanned = rawTracks.map(normalizeTrack);
  const scannedById = new Map(scanned.map(track => [track.id, track]));
  for (let index = state.tracks.length - 1; index >= 0; index -= 1) {
    const existing = state.tracks[index];
    if (existing.sourceType !== 'webdav' || existing.sourceId !== connectionId) continue;
    const track = scannedById.get(existing.id);
    if (!track) state.tracks.splice(index, 1);
    else {
      Vue.set(state.tracks, index, { ...existing, ...track, createdAt: existing.createdAt || track.createdAt });
      scannedById.delete(existing.id);
    }
  }
  for (const track of scannedById.values()) state.tracks.push(track);
  pruneTrackReferences();
  persist();
}
async function loadDavConnections() {
  const connections = cloudflareRuntime ? browserDav.list() : await api('/api/webdav/connections');
  state.davConnections = Array.isArray(connections) ? connections.map(safeDavConnection).filter(connection => connection.id) : [];
  return state.davConnections;
}
async function createDavConnection(input: DavConnectionInput) {
  try {
    const connection = safeDavConnection(cloudflareRuntime ? browserDav.create(input) : await api('/api/webdav/connections', {
      method: 'POST', headers: {'content-type': 'application/json'}, body: JSON.stringify(input),
    }));
    replaceDavConnection(connection);
    notify('WebDAV 连接已保存', 'success');
    return connection;
  } catch (error) {
    notify(`WebDAV 连接保存失败：${(error as Error).message}`, 'error');
    throw error;
  }
}
async function updateDavConnection(id: string, input: Partial<DavConnectionInput>) {
  davScanTickets.delete(id);
  try {
    const previous = state.davConnections.find(connection => connection.id === id);
    const connection = safeDavConnection(cloudflareRuntime ? browserDav.update(id, input) : await api(`/api/webdav/connections/${encodeURIComponent(id)}`, {
      method: 'PATCH', headers: {'content-type': 'application/json'}, body: JSON.stringify(input),
    }));
    davScanTickets.delete(id);
    replaceDavConnection(connection);
    if (previous && (previous.url !== connection.url || (previous.rootPath || '/') !== (connection.rootPath || '/'))) syncDavTracks(id, []);
    notify('WebDAV 连接已更新', 'success');
    return connection;
  } catch (error) {
    notify(`WebDAV 连接更新失败：${(error as Error).message}`, 'error');
    throw error;
  }
}
async function testDavConnection(id: string) {
  try {
    const result = cloudflareRuntime
      ? await api('/api/webdav/test', browserDav.request(id))
      : await api(`/api/webdav/connections/${encodeURIComponent(id)}/test`, { method: 'POST' });
    notify('WebDAV 连接成功', 'success');
    return result;
  } catch (error) {
    notify(`WebDAV 连接失败：${(error as Error).message}`, 'error');
    throw error;
  }
}
async function scanDavConnection(id: string, options: { recursive?: boolean } = { recursive: true }) {
  const ticket = Symbol();
  davScanTickets.set(id, ticket);
  let revision: string | undefined;
  let revisionCaptured = false;
  const isCurrent = () => {
    if (davScanTickets.get(id) !== ticket || !state.davConnections.some(connection => connection.id === id)) return false;
    if (!cloudflareRuntime || !revisionCaptured) return true;
    try { return revision === browserDav.revision(id); }
    catch { return false; }
  };
  try {
    revision = cloudflareRuntime ? browserDav.revision(id) : undefined;
    revisionCaptured = true;
    let result = cloudflareRuntime ? await api('/api/webdav/scan', browserDav.request(id, options)) : await api(`/api/webdav/connections/${encodeURIComponent(id)}/scan`, {
      method: 'POST', headers: {'content-type': 'application/json'}, body: JSON.stringify(options),
    });
    if (!isCurrent()) throw staleDavScan();
    if (cloudflareRuntime) {
      result = mergeBrowserDavScan(state.tracks.filter(track => track.sourceType === 'webdav' && track.sourceId === id), result);
    }
    syncDavTracks(id, Array.isArray(result?.tracks) ? result.tracks : []);
    const total = Number(result?.changes?.total ?? result?.tracks?.length ?? 0);
    notify(`WebDAV 扫描完成，索引 ${total} 首音频`, 'success');
    return result;
  } catch (error) {
    if (!isCurrent() || (error as { code?: string })?.code === 'DAV_SCAN_STALE') throw staleDavScan();
    notify(`WebDAV 扫描失败：${(error as Error).message}`, 'error');
    throw error;
  }
}
async function deleteDavConnection(id: string) {
  davScanTickets.delete(id);
  try {
    if (cloudflareRuntime) browserDav.remove(id);
    else await api(`/api/webdav/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
    davScanTickets.delete(id);
    state.davConnections = state.davConnections.filter(connection => connection.id !== id);
    state.tracks = state.tracks.filter(track => track.sourceType !== 'webdav' || track.sourceId !== id);
    pruneTrackReferences();
    persist();
    notify('WebDAV 连接已删除', 'success');
  } catch (error) {
    notify(`WebDAV 连接删除失败：${(error as Error).message}`, 'error');
    throw error;
  }
}
async function playTracks(tracks: Track[], startId?: string) {
  if (!tracks.length) return notify('这里还没有歌曲', 'info');
  registerTracks(tracks);
  await playTrack(startId || tracks[0].id, tracks.map(track => track.id));
}
async function catalog(kind: string, id?: string | number, options: Record<string, string | number> = {}) {
  const params = new URLSearchParams({ kind, ...Object.fromEntries(Object.entries(options).map(([key,value])=>[key,String(value)])) });
  if (id !== undefined) params.set('id', String(id));
  if (String(id || '').startsWith('qq:')) params.set('platform', 'tx');
  else if (id === undefined && kind !== 'search' && !params.has('platform')) params.set('platform', catalogSettings.platform);
  const data = await api(`/api/catalog?${params}`, { signal: AbortSignal.timeout(20000) });
  if (['playlist','album','artist'].includes(kind)) return { ...data, item: data[kind], tracks: (data.tracks || []).map(normalizeTrack) };
  return data;
}
function commitPlaylists(playlists: Playlist[], tracks = state.tracks, options: { targetIds?: string[]; ownedIds?: string[]; favoriteIds?: string[]; saveError?: string } = {}) {
  const { view: _view, searchQuery: _query, remoteSearchResults: _remoteSearch, notice: _notice, playbackFailure: _failure, currentTime: _time, duration: _duration, isPlaying: _playing, sources: _sources, davConnections: _dav, ...saved } = state;
  const saveError = options.saveError || '浏览器无法保存歌单，修改未提交；请释放站点存储空间后重试。';
  const conflict = () => new Error('另一标签已修改或删除同一歌单、歌曲或喜欢状态，本次修改未提交；请刷新页面后重试。');
  const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
  let snapshot: string | null, latest: typeof saved;
  try { snapshot = localStorage.getItem(STORAGE_KEY); latest = snapshot === null ? saved : JSON.parse(snapshot); }
  catch { throw new Error(saveError); }
  if (!Array.isArray(latest?.tracks) || !Array.isArray(latest?.playlists) || !Array.isArray(latest?.favorites)) throw new Error(saveError);
  const base = new Map(state.playlists.map(playlist => [playlist.id, playlist]));
  const desired = new Map(playlists.map(playlist => [playlist.id, playlist]));
  const current = new Map(latest.playlists.map(playlist => [playlist.id, playlist]));
  const targets = new Set(options.targetIds || []), owned = new Set(options.ownedIds || []);
  const appendOnly = (before: string[], after: string[]) => before.length <= after.length && before.every((id, index) => id === after[index]);
  const mergedExisting = new Map<string, Playlist>();
  for (const [id, before] of base) {
    const after = desired.get(id), shared = current.get(id);
    if (after) for (const trackId of after.trackIds) if (!before.trackIds.includes(trackId)) owned.add(trackId);
    if (!shared) { if (targets.has(id) || !same(before, after)) throw conflict(); continue; }
    if (targets.has(id) && options.ownedIds?.some(trackId => before.trackIds.includes(trackId) && !shared.trackIds.includes(trackId))) throw conflict();
    if (!after) { if (!same(before, shared)) throw conflict(); continue; }
    const merged = { ...shared };
    for (const field of ['name', 'createdAt', 'trackIds'] as const) {
      if (same(before[field], after[field])) continue;
      if (same(before[field], shared[field]) || same(after[field], shared[field])) {
        if (field === 'trackIds') merged.trackIds = [...after.trackIds]; else merged[field] = after[field];
      } else if (field === 'trackIds' && appendOnly(before.trackIds, after.trackIds) && appendOnly(before.trackIds, shared.trackIds)) merged.trackIds = [...new Set([...shared.trackIds, ...after.trackIds])];
      else throw conflict();
    }
    for (const field of ['description', 'customCover', 'coverUpdatedAt'] as const) {
      if (same(before[field], after[field])) continue;
      if (same(before[field], shared[field]) || same(after[field], shared[field])) merged[field] = after[field];
      else throw conflict();
    }
    mergedExisting.set(id, merged);
  }
  // Shared additions retain their order. A deterministic account import may
  // append to an already imported list without replacing its user-edited name.
  let mergedPlaylists = latest.playlists.flatMap(shared => base.has(shared.id) ? mergedExisting.has(shared.id) ? [mergedExisting.get(shared.id)!] : [] : [shared]);
  for (const after of playlists) if (!base.has(after.id)) {
    for (const id of after.trackIds) owned.add(id);
    const shared = current.get(after.id);
    if (shared) {
      if (!targets.has(after.id) && !same(shared, after)) throw conflict();
      const index = mergedPlaylists.findIndex(playlist => playlist.id === after.id);
      mergedPlaylists[index] = { ...shared, trackIds: [...new Set([...shared.trackIds, ...after.trackIds])] };
    } else mergedPlaylists.push({ ...after, trackIds: [...after.trackIds] });
  }
  const updatedAt = new Date().toISOString();
  mergedPlaylists = mergedPlaylists.map(playlist => {
    const previous = current.get(playlist.id);
    return !previous || !same({ ...previous, updatedAt: undefined }, { ...playlist, updatedAt: undefined }) ? { ...playlist, updatedAt } : playlist;
  });
  const beforeTracks = new Set(state.tracks.map(track => track.id)), sharedTracks = new Set(latest.tracks.map(track => track.id));
  // Never resurrect a previously known song deleted in another tab. Catalogue
  // additions keep the latest saved metadata and lyrics for the same original ID.
  for (const id of owned) if (beforeTracks.has(id) && !sharedTracks.has(id)) throw conflict();
  const mergedTracks = [...latest.tracks];
  for (const track of tracks) if (!beforeTracks.has(track.id) && !sharedTracks.has(track.id)) { mergedTracks.push(track); sharedTracks.add(track.id); }
  for (const id of owned) if (!sharedTracks.has(id)) throw conflict();
  const favorites = [...latest.favorites];
  for (const id of options.favoriteIds || []) {
    if (state.favorites.includes(id) && !latest.favorites.includes(id)) throw conflict();
    if (!sharedTracks.has(id)) throw conflict();
    if (!favorites.includes(id)) favorites.push(id);
  }
  const committedTracks = mergedTracks.map(track => owned.has(track.id) && !isLibraryTrack(track) ? { ...track, metadata: { ...track.metadata, libraryOwned: true } } : track);
  if (mergedPlaylists.some(playlist => playlist.customCover !== undefined || playlist.coverUpdatedAt !== undefined)) assertPlaylistCoverBudget(mergedPlaylists);
  const serialized = JSON.stringify({ ...latest, tracks: committedTracks, playlists: mergedPlaylists, favorites });
  // This catches changes observed during preparation; localStorage offers no
  // atomic cross-tab CAS, so simultaneous read/write races remain a separate scope.
  let unchanged: boolean;
  try { unchanged = localStorage.getItem(STORAGE_KEY) === snapshot; } catch { throw new Error(saveError); }
  if (!unchanged) throw conflict();
  try { localStorage.setItem(STORAGE_KEY, serialized); }
  catch { throw new Error(saveError); }
  state.tracks = committedTracks; state.playlists = mergedPlaylists; state.favorites = favorites;
  return { previousPlaylists: latest.playlists, playlists: mergedPlaylists };
}
function createPlaylist(name: string, initialTracks: Track[] = []) {
  const clean = name.trim().slice(0, 200); if (!clean) return null;
  const tracks = [...state.tracks], known = new Set(tracks.map(track => track.id));
  const trackIds: string[] = [], added = new Set<string>();
  for (const raw of initialTracks) {
    if (added.has(raw.id)) continue;
    const track = normalizeTrack(raw);
    if (!known.has(track.id)) { tracks.push(track); known.add(track.id); }
    trackIds.push(track.id); added.add(track.id);
  }
  const id = uid(); commitPlaylists([...state.playlists, { id, name: clean, trackIds, createdAt: new Date().toISOString() }], tracks);
  notify(`已创建歌单「${clean}」`, 'success'); return id;
}
function renamePlaylist(id: string, name: string) {
  const clean = name.trim().slice(0, 200); if (!clean) throw new Error('请输入歌单名称');
  if (!state.playlists.some(playlist => playlist.id === id)) throw new Error('没有找到这个歌单');
  commitPlaylists(state.playlists.map(playlist => playlist.id === id ? { ...playlist, name: clean } : playlist));
  notify(`已重命名为「${clean}」`, 'success');
}
async function updatePlaylistCover(id: string, customCover?: string) {
  const initial = state.playlists.find(playlist => playlist.id === id);
  if (!initial || id.startsWith('account:')) throw new Error('只能更换自建歌单的封面');
  if (customCover !== undefined) await verifyPlaylistCover(customCover);
  const playlist = state.playlists.find(item => item.id === id);
  if (!playlist) throw new Error('这个歌单已被删除，封面未保存');
  if (playlist.customCover !== initial.customCover || playlist.coverUpdatedAt !== initial.coverUpdatedAt) throw new Error('歌单封面已更换，请重新选择图片');
  const coverUpdatedAt = new Date().toISOString();
  commitPlaylists(state.playlists.map(item => item.id === id ? { ...item, customCover, coverUpdatedAt } : item), state.tracks, { targetIds: [id], saveError: '浏览器无法保存封面，原封面已保留；请释放站点存储空间后重试。' });
  notify(customCover ? '歌单封面已更新' : '已恢复自动封面', 'success'); render();
}
function removePlaylists(ids: string[]) {
  if (!Array.isArray(ids) || !ids.length || ids.some(id => typeof id !== 'string')) throw new Error('请选择要删除的歌单');
  const selected = new Set(ids);
  if ([...selected].some(id => !state.playlists.some(playlist => playlist.id === id))) throw new Error('部分歌单已被删除，请刷新后重试');
  commitPlaylists(state.playlists.filter(playlist => !selected.has(playlist.id)), state.tracks, { targetIds: [...selected], saveError: '浏览器无法保存删除操作，原歌单已保留；请释放站点存储空间后重试。' });
  notify(`已删除 ${selected.size} 个歌单`, 'success');
  return selected.size;
}
async function updatePlaylistDetails(id: string, input: { name: string; description: string; customCover?: string | null }) {
  if (typeof input.name !== 'string' || !input.name.trim() || input.name.trim().length > 200) throw new Error('请输入不超过200字的歌单名称');
  if (typeof input.description !== 'string' || input.description.length > 2000) throw new Error('歌单简介不能超过2000字');
  const name = input.name.trim(), description = input.description, customCover = input.customCover;
  const original = state.playlists.find(playlist => playlist.id === id);
  if (!original || /^(?:account:|account-view:|qq:)/.test(id)) throw new Error('只能编辑自建歌单');
  const initial = { name: original.name, description: original.description, customCover: original.customCover, coverUpdatedAt: original.coverUpdatedAt };
  const changesCover = customCover !== undefined;
  if (changesCover && customCover !== null) await verifyPlaylistCover(customCover!);
  const current = state.playlists.find(playlist => playlist.id === id);
  if (!current) throw new Error('歌单已被删除，修改未保存');
  if (Object.keys(initial).some(key => current[key as keyof typeof initial] !== initial[key as keyof typeof initial])) throw new Error('歌单资料已更新，请重新打开编辑');
  const patch = { name, description, ...(changesCover ? { customCover: customCover || undefined, coverUpdatedAt: new Date().toISOString() } : {}) };
  commitPlaylists(state.playlists.map(playlist => playlist.id === id ? { ...playlist, ...patch } : playlist), state.tracks, { targetIds: [id], saveError: '浏览器无法保存歌单资料，原资料已保留；请释放站点存储空间后重试。' });
  notify('歌单资料已保存', 'success'); render();
}
function addTracksToPlaylist(ids: string[], playlistId: string) {
  const playlist = state.playlists.find(item => item.id === playlistId);
  if (!playlist) throw new Error('没有找到这个歌单');
  const tracks = [...state.tracks]; const knownTracks = new Set(tracks.map(track => track.id));
  const trackIds = [...playlist.trackIds]; const knownIds = new Set(trackIds);
  for (const id of ids) {
    if (knownIds.has(id)) continue;
    const track = trackById(id) || state.remoteSearchResults.find(item => item.id === id);
    if (!track) continue;
    if (!knownTracks.has(id)) { tracks.push(track); knownTracks.add(id); }
    trackIds.push(id); knownIds.add(id);
  }
  const added = trackIds.length - playlist.trackIds.length;
  if (!added) return 0;
  commitPlaylists(state.playlists.map(item => item.id === playlistId ? { ...item, trackIds } : item), tracks);
  notify(`已将 ${added} 首歌曲加入「${playlist.name}」`, 'success'); render(); return added;
}
function movePlaylistTracks(trackIds: string[], fromId: string, toId: string) {
  if (!fromId || !toId || fromId === toId) throw new Error('请选择不同的源歌单和目标歌单');
  const from = state.playlists.find(item => item.id === fromId), to = state.playlists.find(item => item.id === toId);
  if (!from || !to || /^(?:qq:|account-view:)/.test(fromId) || /^(?:qq:|account-view:)/.test(toId)) throw new Error('源歌单或目标歌单不可写，请选择自建歌单');
  const ids = [...new Set(trackIds)];
  if (!ids.length || ids.some(id => !from.trackIds.includes(id) || !trackById(id))) throw new Error('歌曲已不在源歌单中，移动未提交');
  const selected = new Set(ids), added = ids.filter(id => !to.trackIds.includes(id)).length;
  const next = state.playlists.map(playlist => playlist.id === fromId ? { ...playlist, trackIds: playlist.trackIds.filter(id => !selected.has(id)) }
    : playlist.id === toId ? { ...playlist, trackIds: [...new Set([...playlist.trackIds, ...ids])] } : playlist);
  commitPlaylists(next, state.tracks, { targetIds: [fromId, toId], ownedIds: ids, saveError: '浏览器无法保存歌单，移动未提交；请释放站点存储空间后重试。' });
  notify(`已将 ${ids.length} 首歌曲移动到「${to.name}」`, 'success'); render(); return { moved: ids.length, added };
}
function importPlatformPlaylist(input: { platform: string; accountId: string; playlistId: string; name: string; kind?: string; tracks: Track[] }) {
  if (!['netease', 'qq'].includes(input.platform) || !/^[A-Za-z0-9_-]{1,128}$/.test(String(input.accountId)) || !/^[A-Za-z0-9_-]{1,128}$/.test(String(input.playlistId))) throw new Error('账号歌单身份无效');
  const id = `account:${input.platform}:${input.accountId}:${input.playlistId}`;
  const incomingIds = new Set(input.tracks.map(track => track.id));
  const tracks = state.tracks.map(track => incomingIds.has(track.id) ? { ...track, metadata: { ...track.metadata, libraryOwned: true } } : track); const ids = new Set(tracks.map(track => track.id));
  for (const raw of input.tracks) { if (!ids.has(raw.id)) { tracks.push(normalizeTrack({ ...raw, metadata: { ...raw.metadata, libraryOwned: true } })); ids.add(raw.id); } }
  const existing = state.playlists.find(item => item.id === id);
  const playlist = existing ? { ...existing, trackIds: [...existing.trackIds] } : { id, name: input.name.trim().slice(0, 200) || '账号歌单', trackIds: [] as string[], createdAt: new Date().toISOString() };
  const known = new Set(playlist.trackIds);
  const additions = [...new Map(input.tracks.filter(item => ids.has(item.id) && !known.has(item.id)).map(item => [item.id, item])).values()];
  for (const track of additions) { if (!known.has(track.id)) { playlist.trackIds.push(track.id); known.add(track.id); } }
  const playlists = existing ? state.playlists.map(item => item.id === id ? playlist : item) : [...state.playlists, playlist];
  const importedIds = input.tracks.map(track => track.id).filter(id => ids.has(id));
  const committed = commitPlaylists(playlists, tracks, { targetIds: [id], ownedIds: importedIds, ...(input.kind === 'liked' ? { favoriteIds: importedIds } : {}), saveError: '浏览器无法保存账号歌单，导入未提交；请释放站点存储空间后重试。' });
  const previousIds = new Set(committed.previousPlaylists.find(item => item.id === id)?.trackIds || []);
  const committedPlaylist = committed.playlists.find(item => item.id === id)!;
  return { id, added: committedPlaylist.trackIds.filter(trackId => !previousIds.has(trackId)).length, total: committedPlaylist.trackIds.length };
}
async function loadSources() {
  try { state.sources = (await api('/api/sources')).map((source: Source) => ({...source, enabled: ui.sourcePreferences[source.id] ?? source.enabled ?? true})); }
  catch { state.sources = [{ id: 'local', name: '浏览器本地', kind: 'local', status: '可用', capabilities: ['import','playback'] },
    ...['yuxi','yuningxi'].map(id => ({id,name: id === 'yuxi' ? '屿溪 · 原生适配' : '玉宁熙 · 原生适配',kind:'online',status:'连接失败',capabilities:[], enabled:ui.sourcePreferences[id] !== false, reason:'无法连接音源服务，请重试'}))]; }
}
async function setSourceEnabled(id: string, enabled: boolean) {
  try {
    if (!cloudflareRuntime) await api(`/api/sources/${id}`, {method:'PATCH',headers:{'content-type':'application/json'},body:JSON.stringify({enabled})});
    Vue.set(ui.sourcePreferences, id, enabled); localStorage.setItem('ypm.sources.v1', JSON.stringify(ui.sourcePreferences));
    const source = state.sources.find(s => s.id === id); if(source) Vue.set(source,'enabled',enabled);
    notify(enabled ? '此浏览器已启用该音源' : '此浏览器已停用该音源','success');
  } catch(error) { notify(String((error as Error).message),'error'); }
}
function sourceDeclaration(value?: Partial<Source>): Pick<Source, 'capabilities' | 'platforms' | 'qualitiesByPlatform'> | null {
  const { capabilities, platforms, qualitiesByPlatform } = value || {};
  if (!Array.isArray(capabilities) || !capabilities.every(validQuality) || !Array.isArray(platforms) || !platforms.every(validQuality)
    || !qualitiesByPlatform || typeof qualitiesByPlatform !== 'object' || Array.isArray(qualitiesByPlatform)) return null;
  const qualities = Object.entries(qualitiesByPlatform);
  if (platforms.some(platform => !Object.hasOwn(qualitiesByPlatform, platform))
    || qualities.some(([platform, values]) => !platforms.includes(platform) || !Array.isArray(values) || !values.every(validQuality))) return null;
  return { capabilities: [...new Set(capabilities)], platforms: [...new Set(platforms)],
    qualitiesByPlatform: Object.fromEntries(qualities.map(([platform, values]) => [platform, [...new Set(values)]])) };
}
async function testSource(id: string) {
  const source = state.sources.find(s=>s.id === id); if(source) source.status='检测中';
  try {
    const result = await api(`/api/sources/${id}/test`, {method:'POST',headers:{'content-type':'application/json'},body:'{}'});
    if ([result.sourceId, result.id].some(value => value !== undefined && value !== id)) throw new Error('音源检测返回的身份不符，请重试');
    const passed = result.ok !== false && result.playback?.ok !== false && (result.ok === true || result.playback?.ok === true);
    let declaration = passed ? sourceDeclaration(result) : null;
    const current = state.sources.find(s => s.id === id);
    if (passed && current && !declaration && !sourceDeclaration(current)) {
      try {
        const sources = await api('/api/sources', { signal: AbortSignal.timeout(15000) });
        // Recover only this source's declared abilities; another test or a
        // browser preference may have changed while the request was pending.
        declaration = sourceDeclaration(state.sources.find(s => s.id === id))
          || sourceDeclaration(Array.isArray(sources) ? sources.find(item => item?.id === id) : undefined);
      } catch { /* A successful probe alone cannot declare platforms or qualities. */ }
    }
    const latest = state.sources.find(s => s.id === id);
    if (latest) {
      if (declaration) for (const [key, value] of Object.entries(declaration)) Vue.set(latest, key, value);
      latest.status = result.status || (result.playback?.ok ? '探测通过' : '检测完成');
      Vue.set(latest, 'reason', result.reason || result.error || result.playback?.error
        || (passed && !sourceDeclaration(latest) ? '检测已通过，但音源能力声明未能载入，请重新载入音源。' : ''));
    }
    notify(latest?.status || '检测完成', 'info');
    return result;
  } catch(error) { const latest = state.sources.find(s => s.id === id); if(latest) { latest.status='检测失败'; Vue.set(latest,'reason',(error as Error).message); } notify((error as Error).message,'error'); throw error; }
}
function prefetchTrackPresentation(id: string) {
  const track = trackById(id); if (!track) return;
  const url = cover(track);
  if (!coverPreloads.has(url)) {
    const image = new Image();
    image.decoding = 'async'; image.src = url; coverPreloads.set(url, image);
    // Only the latest small set of cover requests is retained.
    if (coverPreloads.size > 4) coverPreloads.delete(coverPreloads.keys().next().value!);
  }
  void loadTrackLyrics(id);
}
type LyricTranslationStatus = { status: 'idle' | 'loading' | 'official' | 'generated' | 'unavailable' | 'error'; source: string; error: string };
const lyricTranslations = Vue.observable({ entries: {} as Record<string, LyricTranslationStatus> });
const loadedLyricBundles = new Map<string, string>();
const translationRequests = new Map<string, Promise<boolean>>();
function lyricTranslation(id: string): LyricTranslationStatus {
  const track = trackById(id);
  const current = lyricTranslations.entries[id];
  if (current?.status === 'loading') return current;
  if (track?.amllLyrics?.some(line => line.translatedLyric?.trim())) return {
    status: track.metadata?.lyricTranslationKind === 'generated' ? 'generated' : 'official',
    source: String(track.metadata?.lyricTranslationSource || (track.sourceType === 'online' ? '已载入歌词' : '本地歌词')), error: '',
  };
  return current || { status: 'idle', source: '', error: '' };
}
function setLyricTranslation(id: string, status: LyricTranslationStatus['status'], source = '', error = '') {
  Vue.set(lyricTranslations.entries, id, { status, source, error });
}
const LOCAL_LYRIC_LOOKUP_KEY = 'simple-music.local-lyric-lookup.v1';
const readLocalLyricLookup = () => {
  try { return localStorage.getItem(LOCAL_LYRIC_LOOKUP_KEY) !== 'false'; } catch { return true; }
};
const lyricSettings = Vue.observable({ localAutoLookup: readLocalLyricLookup() });
function setLocalAutoLookup(enabled: boolean) {
  lyricSettings.localAutoLookup = enabled === true;
  try { localStorage.setItem(LOCAL_LYRIC_LOOKUP_KEY, String(lyricSettings.localAutoLookup)); } catch { /* The current session remains usable. */ }
  if (!lyricSettings.localAutoLookup) {
    for (const [id, request] of lyricRequests) {
      if (trackById(id)?.sourceType === 'local') { request.controller.abort(); lyricRequests.delete(id); localLyricAttempts.delete(id); setLyricTranslation(id, 'idle'); }
    }
  }
}
const localLyricAttempts = new Map<string, string>();
type LyricPlatform = 'wy' | 'tx';
async function fetchLyricBundle(platform: LyricPlatform, externalId: string, signal: AbortSignal) {
  const response = await apiFetch(`/api/lyrics/${platform}/${encodeURIComponent(externalId)}?format=bundle`, { signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]) });
  if (!response.ok) throw new Error(`歌词请求失败 (${response.status})`);
  const bundle: OnlineLyricBundle = response.headers.get('content-type')?.includes('application/json')
    ? await response.json().then(value => value.data ?? value)
    : { provider: platform === 'wy' ? 'netease' : 'qq-music', original: { provider: platform === 'wy' ? 'netease' : 'qq-music', extension: response.headers.get('x-lyrics-extension') === 'qrc' ? 'qrc' : 'lrc', fileName: response.headers.get('x-lyrics-extension') === 'qrc' ? 'lyrics.qrc' : 'lyrics.lrc', text: await response.text() }, translation: null, roman: null, warnings: [] };
  const decoded = bundle.original;
  if (!decoded || typeof decoded.text !== 'string' || !['lyrics.lrc', 'lyrics.qrc', 'lyrics.yrc'].includes(decoded.fileName)) throw new Error('平台歌词响应格式无效');
  const lines = parseTimedLyrics(decoded.fileName, decoded.text);
  if (!lines.length) throw new Error('平台未提供可用的时间轴歌词');
  return { bundle, decoded, lines };
}
const hasLocalLyrics = (track?: Track) => Boolean(track?.lyrics?.length || track?.amllLyrics?.length || (typeof track?.metadata?.embeddedPlainLyrics === 'string' && track.metadata.embeddedPlainLyrics.trim() && !hasMisreadLyricist(track.metadata.embeddedLyricsSources)));
async function loadLocalTrackLyrics(id: string, track: Track): Promise<void> {
  if (!lyricSettings.localAutoLookup || track.availability === 'unavailable' || hasLocalLyrics(track)) return;
  // Old persistent indexes may not have restored their embedded lyrics yet.
  // Share the bounded metadata queue before deciding that online lookup is needed.
  await requestLocalPresentation(id);
  const restored = trackById(id);
  if (!restored || restored !== track || !lyricSettings.localAutoLookup || restored.availability === 'unavailable' || hasLocalLyrics(restored)) return;
  const pending = lyricRequests.get(id); if (pending) return pending.promise;
  const query = localLyricLookupQuery(track);
  if (!query) return;
  const attemptKey = localLyricAttemptKey(track);
  if (!reserveLocalLyricAttempt(localLyricAttempts, id, attemptKey)) return;
  if (lyricRequests.size >= 3) {
    const oldest = lyricRequests.entries().next().value;
    if (oldest) { oldest[1].controller.abort(); lyricRequests.delete(oldest[0]); localLyricAttempts.delete(oldest[0]); setLyricTranslation(oldest[0], 'idle'); }
  }
  const source = '在线歌词匹配';
  setLyricTranslation(id, 'loading', source);
  const controller = new AbortController();
  const promise = (async () => {
    try {
      const sourceIds = ['yuxi', 'yuningxi'].filter(sourceId => ui.sourcePreferences[sourceId] !== false);
      if (!sourceIds.length) throw new Error('没有启用可查找歌词的在线音源');
      const searchResponse = await apiFetch(`/api/search?${new URLSearchParams({ q: query, sources: sourceIds.join(','), platform: 'all', type: 'track', cursor: '0' })}`, { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
      if (!searchResponse.ok) throw new Error(`歌词搜索失败 (${searchResponse.status})`);
      const searchPayload = await searchResponse.json().then(value => value.data ?? value);
      const candidate = selectLocalLyricCandidate(track, Array.isArray(searchPayload?.results) ? searchPayload.results : []);
      if (!candidate) throw new Error('未找到匹配的在线歌词');
      const platform: LyricPlatform = candidate.metadata?.platform === 'tx' || String(candidate.id || '').split(':')[2] === 'tx' ? 'tx' : 'wy';
      const externalId = String(platform === 'tx' ? candidate.metadata?.songId || candidate.externalId : candidate.externalId || '');
      if (!externalId) throw new Error('匹配结果缺少歌曲编号');
      const { bundle, decoded, lines } = await fetchLyricBundle(platform, externalId, controller.signal);
      if (controller.signal.aborted) return;
      const current = trackById(id);
      if (!current || current !== track || current.sourceType !== 'local' || hasLocalLyrics(current)) return;
      const original = parseAmllLyrics(decoded.fileName, decoded.text, lines);
      const amllLyrics = mergeLyricLayers(original, bundle.translation, bundle.roman);
      const originalLines = lines;
      Vue.set(current, 'lyrics', originalLines); Vue.set(state.lyrics, id, originalLines);
      Vue.set(current, 'amllLyrics', amllLyrics.length ? amllLyrics : undefined);
      Vue.set(current, 'metadata', { ...current.metadata, localLyricSource: platform === 'wy' ? '网易云音乐' : 'QQ音乐', localLyricTrackId: candidate.id, localLyricFetchedAt: new Date().toISOString() });
      loadedLyricBundles.set(id, `local:${candidate.id}`);
      const translated = amllLyrics.some(line => line.translatedLyric?.trim());
      const translationWarning = Array.isArray(bundle.warnings) ? bundle.warnings.find(warning => warning.startsWith('TRANS_')) : '';
      setLyricTranslation(id, translated ? 'official' : translationWarning ? 'error' : 'unavailable', platform === 'wy' ? '网易云音乐' : 'QQ音乐', translated ? '' : translationWarning ? `平台译文解析失败（${translationWarning}）` : '平台未提供该歌曲的翻译');
      current.updatedAt = new Date().toISOString();
      persist();
      if (ui.lyricsOpen) Vue.nextTick(() => { if (state.currentId === id) mountAmllLyrics(); });
    } catch (error) {
      if (lyricRequests.get(id)?.controller === controller) setLyricTranslation(id, controller.signal.aborted ? 'idle' : 'error', source, controller.signal.aborted ? '' : (error as Error).message);
    } finally {
      if (lyricRequests.get(id)?.controller === controller) {
        lyricRequests.delete(id);
        const current = trackById(id);
        if (!hasLocalLyrics(current)) releaseLocalLyricAttempt(localLyricAttempts, id, attemptKey);
      } else if (!lyricRequests.has(id) && !hasLocalLyrics(trackById(id))) {
        // The concurrency cap or settings may have evicted this request
        // before its promise settled. It must remain retryable as well.
        releaseLocalLyricAttempt(localLyricAttempts, id, attemptKey);
      }
    }
  })();
  lyricRequests.set(id, { controller, promise });
  return promise;
}
function loadTrackLyrics(id: string): Promise<void> {
  const track = trackById(id); if (!track) return Promise.resolve();
  if (track.sourceType === 'local') return loadLocalTrackLyrics(id, track);
  if (track.sourceType !== 'online' || track.metadata?.neteaseCloud?.accountOnly === true) return Promise.resolve();
  const platform = onlinePlatform(track);
  const externalId = String(platform === 'wy' ? track.externalId : track.metadata?.songId || track.externalId);
  const bundleKey = `${platform}:${externalId}`;
  if (loadedLyricBundles.get(id) === bundleKey) return Promise.resolve();
  const pending = lyricRequests.get(id); if (pending) return pending.promise;
  // A quick track switch can finish the old track's cache, but cannot overwrite
  // the current track. Bound concurrent requests when skipping rapidly.
  if (lyricRequests.size >= 3) {
    const oldest = lyricRequests.entries().next().value;
    if (oldest) { oldest[1].controller.abort(); lyricRequests.delete(oldest[0]); setLyricTranslation(oldest[0], 'idle'); }
  }
  const source = platform === 'wy' ? '网易云音乐' : 'QQ音乐';
  setLyricTranslation(id, 'loading', source);
  const controller = new AbortController();
  const promise = (async () => {
  try {
    const { bundle, decoded, lines } = await fetchLyricBundle(platform, externalId, controller.signal);
    if (controller.signal.aborted) return;
    const current = trackById(id);
    if (!current || current !== track || current.sourceType !== 'online' || onlinePlatform(current) !== platform || String(platform === 'wy' ? current.externalId : current.metadata?.songId || current.externalId) !== externalId) return;
    // Existing manual/sidecar originals and their word timing take priority.
    const original = current.amllLyrics?.length ? current.amllLyrics : current.lyrics?.length ? toAmllLines(current.lyrics) : parseAmllLyrics(decoded.fileName, decoded.text, lines);
    const hadTranslation = original.some(line => line.translatedLyric?.trim());
    const amllLyrics = mergeLyricLayers(original, bundle.translation, bundle.roman);
    if (!current.lyrics?.length) {
      const originalLines = original.map(line => ({ time: line.startTime / 1000, text: line.words.map(word => word.word).join('') }));
      Vue.set(current, 'lyrics', originalLines); Vue.set(state.lyrics, id, originalLines);
    }
    Vue.set(current, 'amllLyrics', amllLyrics.length ? amllLyrics : undefined);
    const translated = amllLyrics.some(line => line.translatedLyric?.trim());
    if (translated && !hadTranslation) Vue.set(current, 'metadata', { ...current.metadata, lyricTranslationSource: source, lyricTranslationKind: 'official' });
    loadedLyricBundles.set(id, bundleKey);
    const translationWarning = Array.isArray(bundle.warnings) ? bundle.warnings.find(warning => warning.startsWith('TRANS_')) : '';
    setLyricTranslation(id, translated ? 'official' : translationWarning ? 'error' : 'unavailable', source, translated ? '' : translationWarning ? `平台译文解析失败（${translationWarning}）` : bundle.translation ? '平台译文与当前歌词时间轴不匹配' : '平台未提供该歌曲的翻译');
    current.updatedAt = new Date().toISOString();
    persist();
    if (ui.lyricsOpen) Vue.nextTick(() => { if (state.currentId === id) mountAmllLyrics(); });
  } catch (error) {
    // Presentation failures remain visible without stopping a playable track.
    if (lyricRequests.get(id)?.controller === controller) setLyricTranslation(id, controller.signal.aborted ? 'idle' : 'error', source, controller.signal.aborted ? '' : (error as Error).message);
  } finally {
    if (lyricRequests.get(id)?.controller === controller) lyricRequests.delete(id);
  }
  })();
  lyricRequests.set(id, { controller, promise });
  return promise;
}
function requestLyricTranslation(id = state.currentId || ''): Promise<boolean> {
  const pending = translationRequests.get(id); if (pending) return pending;
  const operation = (async () => {
    const initial = trackById(id); if (!initial) return false;
    if (initial.sourceType === 'online' && initial.metadata?.neteaseCloud?.accountOnly !== true) await loadTrackLyrics(id);
    const track = trackById(id); if (!track || track !== initial) return false;
    if (lyricTranslation(id).status === 'official' || lyricTranslation(id).status === 'generated') return true;
    const original = track.amllLyrics?.length ? track.amllLyrics : toAmllLines(track.lyrics || []);
    const lines = original.map(line => line.words.map(word => word.word).join('').trim()).filter(Boolean);
    if (!lines.length) { setLyricTranslation(id, 'unavailable', '', '当前歌曲没有可翻译的原词'); return false; }
    if (lines.length > 300 || lines.some(line => line.length > 500) || lines.join('').length > 12000) {
      setLyricTranslation(id, 'error', '', '歌词超过翻译接口的文本限制，未上传原词'); return false;
    }
    const signature = lyricOriginalSignature(original);
    setLyricTranslation(id, 'loading', '在线翻译');
    try {
      // This upload occurs only after the explicit menu action, including for
      // local files; automatic presentation prefetch never sends local lyrics.
      const translatedResponse = await apiFetch('/api/translate', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ lines, target: 'zh' }), signal: AbortSignal.timeout(120000) });
      const payload = await translatedResponse.json().catch(() => null);
      if (!translatedResponse.ok) throw new Error(typeof payload?.error === 'string' ? payload.error : payload?.error?.message || `翻译请求失败 (${translatedResponse.status})`);
      const response = payload?.data ?? payload;
      const current = trackById(id);
      const currentLines = current?.amllLyrics?.length ? current.amllLyrics : toAmllLines(current?.lyrics || []);
      if (current !== track || lyricOriginalSignature(currentLines) !== signature) throw new Error('歌词已更换，请重新获取翻译');
      const translated = applyGeneratedTranslation(currentLines, response);
      const source = String(response?.by || '在线翻译');
      Vue.set(track, 'amllLyrics', translated);
      Vue.set(track, 'metadata', { ...track.metadata, lyricTranslationKind: 'generated', lyricTranslationSource: source });
      setLyricTranslation(id, 'generated', source);
      track.updatedAt = new Date().toISOString(); persist();
      if (ui.lyricsOpen) Vue.nextTick(() => { if (state.currentId === id) mountAmllLyrics(); });
      return true;
    } catch (error) { setLyricTranslation(id, 'error', '', (error as Error).message); return false; }
  })().finally(() => { translationRequests.delete(id); });
  translationRequests.set(id, operation);
  return operation;
}
function exportBackup() {
  try {
    const blob = new Blob([serializeBackup({ ...state, currentId: currentTrack() ? state.currentId : null, version: 1, exportedAt: new Date().toISOString() })], {type:'application/json'});
    const link=document.createElement('a'); link.href=URL.createObjectURL(blob);link.download=`music-library-${new Date().toISOString().slice(0,10)}.json`;link.click();setTimeout(()=>URL.revokeObjectURL(link.href),1000);
    notify('备份已导出','success');
  } catch(error) { notify((error as Error).message,'error'); }
}
function libraryCloudSnapshot() {
  if (persistencePending) throw new Error('本地音乐库尚未保存，请先重试保存或导出备份。');
  const stored = localStorage.getItem(STORAGE_KEY);
  const saved = stored === null ? readSaved() : JSON.parse(stored) as SavedState;
  if (!Array.isArray(saved.tracks) || !Array.isArray(saved.playlists)) throw new Error('无法读取已保存的音乐库索引');
  // Local presentation data may have been compacted into IndexedDB. Include
  // only successful cache writes/readbacks, never a pending in-memory edit.
  const tracks = saved.tracks.map(track => {
    const cache = persistedLocalPresentations.get(track.id);
    if (track.sourceType !== 'local' || track.metadata?.localIndexLyricsOverride === true || !cache || cache.fingerprint !== track.fingerprint || cache.size !== track.size) return track;
    const texts = Object.fromEntries(Object.entries({ embeddedPlainLyrics: cache.lyrics.plainText, embeddedTranslation: cache.lyrics.translationText, embeddedRoman: cache.lyrics.romanText }).filter(([, value]) => typeof value === 'string'));
    return { ...track, lyrics: track.lyrics || cache.lyrics.lyrics, amllLyrics: track.amllLyrics || cache.lyrics.amllLyrics, metadata: { ...texts, ...track.metadata } };
  });
  return libraryCloudInput({ ...saved, tracks, favorites: saved.favorites || [], history: saved.history || [], historyPlayedAt: saved.historyPlayedAt || {}, lyrics: saved.lyrics || {} });
}
async function mergeCloudLibrary(backup: MetadataBackup, guard: { expectedSnapshot: string; isCurrent: () => boolean }) {
  if (state.importTask?.status === 'running') throw new Error('请先结束当前导入，再合并云端索引。');
  for (const entry of [...backup.playlists, ...backup.tracks]) if (entry.customCover) await verifyPlaylistCover(entry.customCover);
  const conflict = () => new Error('本地音乐库或账号已更新，合并未提交，请重试。');
  if (!guard.isCurrent() || serializeLibraryCloudSnapshot(libraryCloudSnapshot()) !== guard.expectedSnapshot) throw conflict();
  const snapshot = localStorage.getItem(STORAGE_KEY);
  const latest: SavedState = snapshot === null ? readSaved() : JSON.parse(snapshot);
  const durable = new Map(libraryCloudSnapshot().tracks.map(track => [track.id, track]));
  const merged = mergeLibraryCloudIndex({ ...latest, tracks: latest.tracks.map(track => durable.get(track.id) || track) }, backup);
  const mergedIds = new Set(merged.tracks.map(track => track.id));
  if ([state.currentId, ...state.queue].some(id => id && !mergedIds.has(id) && state.tracks.some(track => track.id === id))) throw new Error('当前播放列表已在其他页面更新，请刷新音乐库后重试合并；当前播放保持不变。');
  const next = JSON.stringify({ ...latest, ...merged });
  if (!guard.isCurrent() || localStorage.getItem(STORAGE_KEY) !== snapshot) throw conflict();
  try { localStorage.setItem(STORAGE_KEY, next); }
  catch { throw new Error('浏览器无法保存合并结果，本地音乐库保持不变。请释放存储空间后重试。'); }
  if (lastFullStateSnapshot === snapshot) lastFullStateSnapshot = next;
  const live = new Map(state.tracks.map(track => [track.id, track]));
  // Preserve current media objects, temporary cover URLs and local availability.
  state.tracks = merged.tracks.map(track => {
    const existing = live.get(track.id);
    if (!existing) return normalizeTrack(track);
    const artworkRef = existing.artworkRef, availability = existing.availability;
    Object.assign(existing, track, { artworkRef, availability });
    return existing;
  });
  state.favorites = merged.favorites; state.playlists = merged.playlists;
  state.history = merged.history; state.historyPlayedAt = merged.historyPlayedAt; state.lyrics = merged.lyrics;
  render();
}
async function restoreBackup(file: File) {
  try {
    if(state.importTask?.status==='running') throw new Error('请先结束当前导入');
    if(file.size > 5*1024*1024) throw new Error('备份超过 5 MiB');
    const {version:_v,exportedAt:_d,amllLyrics:_a,...restored}=parseBackup(await file.text());
    restored.tracks = migrateLibraryMembership(restored.tracks, restored.favorites, restored.playlists);
    for (const playlist of restored.playlists) if (playlist.customCover) await verifyPlaylistCover(playlist.customCover);
    for (const track of restored.tracks) if (track.customCover) await verifyPlaylistCover(track.customCover);
    if(!confirm(`恢复 ${restored.tracks.length} 首歌曲和 ${restored.playlists.length} 个歌单？会替换当前浏览器索引，原始文件不受影响。`)) return;
    const snapshot = JSON.stringify(restored);
    try { localStorage.setItem(STORAGE_KEY, snapshot); }
    catch { throw new Error('浏览器无法保存恢复后的音乐库，原音乐库和封面已保留；请释放站点存储空间后重试。'); }
    lastFullStateSnapshot = snapshot;
    cancelPlaybackRequest();audio.pause();audio.removeAttribute('src');audio.load();objectUrls.revokeAll();sessionFiles.clear();
    for(const url of artworkUrls.values()) URL.revokeObjectURL(url);artworkUrls.clear();
    Object.assign(state,restored,{currentTime:0,duration:0,isPlaying:false,importTask:null,remoteSearchResults:[]});
    audio.volume=state.volume;document.documentElement.dataset.theme=state.theme==='system'?(matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'):state.theme;render();notify('备份已恢复，本地音频请重新授权','success');
  } catch(error) {notify((error as Error).message,'error');}
}
async function bootstrap(requireSession = false) {
  if(!cloudflareRuntime) {
    const payload = await window.fetch(apiRequestUrl('/api/session'), requireSession ? { signal: AbortSignal.timeout(20000) } : undefined).then(r=>r.ok?r.json():null).catch(error=>{if(requireSession)throw error;return null;});
    if (requireSession && payload?.data?.authenticated !== true) throw new Error('登录会话未能确认，请检查浏览器是否允许本站 Cookie 后重试。');
    access.configured=payload?.data?.configured === true;access.authenticated=!access.configured || payload?.data?.authenticated === true;
  }
  access.ready=true;
  if(access.authenticated) await Promise.all([loadSources(),hydratePersistentTracks()]);
}

/** Rebuild only a safe local or same-origin relay owner; direct remote streams stay native. */
async function applyEqualizerToCurrentTrack() {
  const id = state.currentId;
  const track = id ? trackById(id) : undefined;
  const relay = track?.sourceType === 'online' && onlinePlayback.resolved?.trackId === id && onlinePlayback.resolved.transport === 'relay';
  if (!id || !track || !audio.getAttribute('src') || track.sourceType !== 'local' && !relay) {
    if (equalizer.enabled && track?.sourceType === 'online') markEqualizerBypass();
    return;
  }
  cancelQualitySwitch();
  const previous = audio, url = previous.getAttribute('src')!, previousSignal = activeMediaSignal;
  const oldBound = isEqualizerBound(previous), oldResolution = onlinePlayback.resolved;
  const baseline = { resolution: oldResolution, url, position: currentPlaybackTime(), autoplay: !previous.paused, playbackRate: previous.playbackRate };
  const task: QualitySwitchTask = { trackId: id, controller: new AbortController(), candidate: null, phase: 'prepare', intent: null, baseline };
  qualitySwitchTask = task;
  const candidate = new Audio(); candidate.preload = 'auto'; candidate.muted = true; candidate.volume = 0; task.candidate = candidate;
  const current = () => qualitySwitchTask === task && state.currentId === id;
  try {
    if (equalizer.enabled && !attachLocalEqualizer(candidate, url, relay)) throw new Error('音效处理器不可用');
    candidate.src = url; candidate.load();
    let latest: NativeOutputIntent;
    const readIntent = (): NativeOutputIntent => latest = { position: currentPlaybackTime(), autoplay: task.intent ? task.intent.autoplay : !audio.paused, playbackRate: task.intent?.playbackRate || audio.playbackRate, volume: state.volume, muted: ui.muted, revision: ui.positionRevision };
    task.candidate = null;
    await nativeOutput.promote(candidate, { signal: task.controller.signal, current, intent: readIntent,
      activated: () => { task.phase = 'handoff'; task.nativePromotion = true; task.intent = { id, autoplay: latest.autoplay, startPosition: latest.position, playbackRate: latest.playbackRate, preserveCurrentState: true }; protectedAudioId = id; activeMediaSignal = task.controller.signal; setEqualizerOwner(audio); },
      restored: () => { protectedAudioId = id; activeMediaSignal = previousSignal; onlinePlayback.resolved = oldResolution; task.intent = null; setEqualizerOwner(audio); state.currentTime = audio.currentTime; state.isPlaying = !audio.paused; },
    });
    detachEqualizer(previous); setEqualizerOwner(audio);
    state.currentTime = audio.currentTime; state.isPlaying = !audio.paused; persist(); render();
  } catch (error) {
    if (audio !== candidate) detachEqualizer(candidate);
    if (current()) { equalizer.enabled = oldBound; setEqualizerOwner(audio); equalizer.message = '音效切换失败，已保留当前播放。'; }
  } finally {
    if (task.candidate) { detachEqualizer(task.candidate); releaseNativeMedia(task.candidate); task.candidate = null; }
    if (qualitySwitchTask === task) qualitySwitchTask = null;
  }
}
export const runtime = {
  readAudioCacheStatus,
  libraryCloudSnapshot, mergeCloudLibrary,
  libraryTracks: () => state.tracks.filter(isLibraryTrack),
  state, ui, access, capabilities, cloudflareRuntime, get audio() { return audio; }, currentPlaybackTime, api, catalog, catalogSettings, setCatalogPlatform, normalizeTrack, registerTracks, playTracks, play:playTrack, applyEqualizerToCurrentTrack,
  onlinePlayback, onlineSelection, onlineSourcesFor, onlineQualitiesFor, defaultQualityOptions, defaultOnlineQuality, qualityLabel, setDefaultQuality, switchOnlinePlayback, setOnlineTransport, resolveOnlineRequest, pausePlaybackForNavigation,
  togglePlay, pauseCurrentPlayback, toggleMute, nextTrack, reservePlaybackRequest, isPlaybackRequestCurrent, seek:seekAudio, toggleFavorite, enqueue:addToQueue, enqueueTracks:addTracksToQueue,
  playNext(id:string) { addToPlayNext(id); ui.playNextIds=[...playbackController.snapshot().playNext]; },
  playbackSnapshot() { syncPlaybackController(); return playbackController.snapshot(); },
  clearPlayNext() { playbackController.clearPlayNext(); ui.playNextIds=[]; persist(); },
  removePlayNext(id: string) { playbackController.setPlayNext(playbackController.getPlayNext().filter(trackId => trackId !== id)); ui.playNextIds = playbackController.getPlayNext(); persist(); },
  addToPlaylist, addTracksToPlaylist, movePlaylistTracks, createPlaylist, renamePlaylist, updatePlaylistCover, updatePlaylistDetails, importPlatformPlaylist, removeTrack,
  removePlaylist(id:string) {return removePlaylists([id]);},removePlaylists,
  removeFromPlaylist(id:string,playlistId:string) {const playlist=state.playlists.find(p=>p.id===playlistId);if(playlist) commitPlaylists(state.playlists.map(item=>item.id===playlistId?{...item,trackIds:item.trackIds.filter(trackId=>trackId!==id)}:item));},
  updateTrack,
  updateLocalTrackDetails,
  importFiles,restoreLocalFile,bindLyrics,cancelImport(){activeImportAbort?.abort();}, setTheme,setLyricOffset,adjustLyricOffset,setPlaybackRate,setMotionLevel,
  setVolume(value:number){state.volume=Math.max(0,Math.min(1,Number(value)));audio.volume=state.volume;persist();},
  setPlayMode(mode: PlaybackMode) { state.playMode = mode; persist(); },
  cyclePlayMode,modeLabel,cover,coverCandidate,formatTime,folderName,notify,retryPlayback,retryPersist,clearPlaybackHistory,exportBackup,restoreBackup,clearAudioCache,clearTrackAudioCache,audioCache,refreshAudioCache,setAudioCacheLimit,setAudioCacheAutoCleanup,effectiveAmllLyrics,
  setSourceEnabled,testSource,loadSources,loadTrackLyrics,lyricTranslation,requestLyricTranslation,lyricSettings,setLocalAutoLookup,loadDavConnections,createDavConnection,updateDavConnection,testDavConnection,scanDavConnection,deleteDavConnection,bootstrap,mountAmllLyrics,disposeAmllLyrics,
  removeQueue(id:string){if(id===state.currentId)return removeCurrentFromQueue();const list=activePlaybackList().filter(t=>t!==id);state.queue=state.queue.filter(t=>t!==id);preferredListForPlay=list;syncPlaybackController();persist();render();},
  removeCurrentFromQueue,
  clearQueue(){state.queue=[];preferredListForPlay=state.currentId?[state.currentId]:[];playbackController.clearPlayNext();ui.playNextIds=[];syncPlaybackController();persist();render();},
  async login(token:string) {await api('/api/session',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({token}),signal:AbortSignal.timeout(20000)});await bootstrap(true);},
};

function bindActiveAudio(media: HTMLAudioElement, current: () => boolean): () => void {
  const subscriptions: (() => void)[] = [];
  const on = (type: string, listener: () => void) => {
    const guarded = () => { if (current()) listener(); };
    media.addEventListener(type, guarded);
    subscriptions.push(() => media.removeEventListener(type, guarded));
  };
  const stopListening = trackListening(media, () => state.currentId, current);
  on('timeupdate', () => {
    if (media.readyState < media.HAVE_METADATA || !state.currentId || protectedAudioId !== state.currentId) return;
    const position = Number(media.currentTime);
    if (!Number.isFinite(position) || position < 0) return;
    state.currentTime = position;
    const duration = Number(media.duration);
    if (Number.isFinite(duration) && duration > 0) state.duration = duration;
    state.positions[state.currentId] = position;
    if (Date.now() - lastProgressPersistAt > 1000) { lastProgressPersistAt = Date.now(); persistPlaybackPosition(); }
    syncMediaSession();
  });
  on('play', () => {
    if (!state.currentId || protectedAudioId !== state.currentId) return;
    state.isPlaying = true; amllLyricPlayer?.resume();
  });
  on('playing', () => {
    if (media.paused || media.readyState < 2 || !state.currentId || protectedAudioId !== state.currentId || !trackById(state.currentId)) return;
    const id = state.currentId, playedAt = Date.now();
    Object.assign(state, recordHistoryPlayback(state.history, state.historyPlayedAt, id, playedAt));
    persistPlaybackHistory(id, playedAt);
    if (trackById(id)?.sourceType === 'local') void manageAudioCache({ touch: id }).catch(() => {});
    if (!document.hidden) playbackController.prefetchNext();
  });
  on('pause', () => {
    playbackController.clearPrefetch();
    if (!state.currentId || protectedAudioId !== state.currentId) return;
    state.isPlaying = false; amllLyricPlayer?.pause(); persistPlaybackPosition();
    if (audioCache.limit !== null) void manageAudioCache().catch(() => {});
  });
  on('ended', () => {
    if (!state.currentId || protectedAudioId !== state.currentId) return;
    delete state.positions[state.currentId]; nextTrack(1, 'ended'); ui.playNextIds = [...playbackController.snapshot().playNext];
  });
  on('error', () => {
    const id = state.currentId;
    if (!id || protectedAudioId !== id) return;
    state.isPlaying = false;
    if (qualitySwitchTask?.trackId === id && qualitySwitchTask.phase !== 'prepare') return;
    if (playbackIntent?.id === id && shouldRelayNativePlayback(onlinePlayback.resolved, id, null, media.error?.code)) return;
    rememberPlaybackFailure(id, 'play', '音频流不可解码或已过期，请重新选择歌曲');
    notify('音频流不可解码或已过期，请重新选择歌曲', 'error');
  });
  on('loadedmetadata', () => {
    if (!state.currentId || protectedAudioId !== state.currentId) return;
    const duration = Number(media.duration);
    if (Number.isFinite(duration) && duration > 0) state.duration = duration;
    media.playbackRate = playbackIntent?.playbackRate || ui.playbackRate;
  });
  return () => { stopListening(); for (const unsubscribe of subscriptions) unsubscribe(); };
}
audio.id = 'music-audio'; audio.preload = 'metadata'; audio.setAttribute('aria-hidden', 'true'); document.body.append(audio);
nativeOutput = new NativeAudioOutput(audio, {
  bind: bindActiveAudio,
  change(previous, current) {
    audio = current;
    previous.removeAttribute('id'); current.id = 'music-audio'; current.setAttribute('aria-hidden', 'true');
    previous.replaceWith(current);
  },
});
window.addEventListener('pagehide',()=>{cancelQualitySwitch();persistPlaybackPosition();});
if (import.meta.hot) import.meta.hot.dispose(cancelQualitySwitch);
document.addEventListener('visibilitychange',()=>{if(document.hidden)playbackController.clearPrefetch();else if(!audio.paused)playbackController.prefetchNext();});
matchMedia('(prefers-color-scheme: dark)').addEventListener('change',()=>{if(state.theme==='system')setTheme('system');});
setTheme(state.theme);
